import { useState, useEffect, useCallback, useMemo } from 'react';
import { XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { Portal } from '../components/Portal';
import { useTenant } from '../contexts/TenantContext';
import { formatDuration } from '../utils/formatDuration';
import { SimpleTooltip } from '../components/ui/SimpleTooltip';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

type MessageItemContent =
  | { text: string; variations?: string[] }
  | { url: string; caption?: string; mediaVariations?: Array<{ url: string; caption?: string; fileName?: string }> }
  | { url: string; fileName?: string }
  | { waitTime: number }
  | { model: string; system: string; user: string };

type MessageContent =
  | { text: string }
  | { url: string; caption?: string }
  | { url: string; fileName?: string }
  | { sequence: Array<{ type: string; content: MessageItemContent }> };

interface Campaign {
  id: string;
  nome: string;
  targetTags: string[];
  sessionNames: string[];
  sessionName: string;
  messageType: string;
  messageContent: MessageContent;
  randomDelay: number;
  startImmediately: boolean;
  scheduledFor: string | null;
  status: string;
  totalContacts: number;
  sentCount: number;
  failedCount: number;
  startedAt: string | null;
  completedAt: string | null;
  createdBy: string | null;
  createdByName: string | null;
  criadoEm: string;
  pausedReason: string | null;
  batchSize: number | null;
  batchStartSentCount: number | null;
  dailyCutoff: string | null;
  deadlineAt: string | null;
  deliveryMode: 'SEQUENCE' | 'VARIATIONS';
  session: {
    name: string;
    displayName?: string;
    status: string;
    mePushName: string | null;
  };
  _count: {
    messages: number;
  };
}

interface WhatsAppSession {
  name: string; // Nome real usado na API
  displayName?: string; // Nome exibido ao usuário
  mePushName: string | null;
  meId: string | null;
}

interface ContactTag {
  id: string;
  nome: string;
  count: number;
}

interface MessageReport {
  id: string;
  status: string;
  errorMessage: string | null;
  sentAt: string | null;
  sessionName: string | null;
  contactName: string;
  contactPhone: string;
}

interface FailureReasonSummary {
  reason: string;
  count: number;
}

interface ReportData {
  campaign: {
    id: string;
    nome: string;
    status: string;
    totalContacts: number;
    sentCount: number;
    failedCount: number;
    messages: MessageReport[];
    createdByName?: string | null;
    criadoEm?: string;
    startedAt?: string | null;
    completedAt?: string | null;
    sessionNames?: string[];
    targetTags?: string[];
    randomDelay?: number;
    batchSize?: number | null;
    dailyCutoff?: string | null;
    deadlineAt?: string | null;
    startImmediately?: boolean;
    scheduledFor?: string | null;
    messageType?: string;
    messageContent?: MessageContent;
    sessionName?: string | null;
  };
  failureReasons?: FailureReasonSummary[];
  messagesBySession: {
    [sessionName: string]: {
      messages: MessageReport[];
    };
  };
  stats?: {
    total: number;
    sent: number;
    failed: number;
    pending: number;
  };
  sessionsInfo?: {
    [sessionName: string]: {
      provider?: string;
      mePushName?: string | null;
      status?: string | null;
    };
  };
}

export function CampaignsPage() {
  const { selectedTenantId } = useTenant();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'general' | 'parameters' | 'messages' | 'summary'>('general');
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [currentReportCampaignId, setCurrentReportCampaignId] = useState<string | null>(null);
  const [reportCurrentPage, setReportCurrentPage] = useState(1);
  const [reportItemsPerPage] = useState(8);
  const [searchTerm, setSearchTerm] = useState('');

  // Confirmation Dialog State
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmDialogConfig, setConfirmDialogConfig] = useState<{
    title: string;
    message: string;
    onConfirm: () => void;
    confirmText: string;
    type: 'danger' | 'info';
  }>({
    title: '',
    message: '',
    onConfirm: () => { },
    confirmText: 'Confirmar',
    type: 'info'
  });

  const showConfirm = (config: {
    title: string;
    message: string;
    onConfirm: () => void;
    confirmText?: string;
    type?: 'danger' | 'info';
  }) => {
    setConfirmDialogConfig({
      title: config.title,
      message: config.message,
      onConfirm: config.onConfirm,
      confirmText: config.confirmText || 'Confirmar',
      type: config.type || 'info'
    });
    setConfirmDialogOpen(true);
  };

  const [contactTags, setContactTags] = useState<ContactTag[]>([]);
  const [whatsappSessions, setWhatsappSessions] = useState<WhatsAppSession[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<{ [key: number]: boolean }>({});
  const [fileInfos, setFileInfos] = useState<{ [key: number]: { name: string, size: number, type: string } }>({});
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  // Form states
  const [formData, setFormData] = useState({
    nome: '',
    targetTags: [] as string[],
    sessionNames: [] as string[],
    sessionName: '',
    messageType: 'sequence',
    messageContent: { sequence: [] as Array<{ type: string; content: MessageItemContent }> } as MessageContent,
    deliveryMode: 'SEQUENCE' as 'SEQUENCE' | 'VARIATIONS',
    randomDelay: 30,
    startImmediately: true,
    scheduledFor: '',
    batchSize: null as number | null,
    dailyCutoff: '',
    deadlineAt: ''
  });

  // Helper para fazer requisições autenticadas
  const authenticatedFetch = useCallback(async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    if (selectedTenantId) {
      (headers as Record<string, string>)['X-Tenant-Id'] = selectedTenantId;
    }

    return fetch(url, {
      ...options,
      headers,
    });
  }, [selectedTenantId]);

  const loadCampaigns = useCallback(async () => {
    try {
      setLoading(true);
      const response = await authenticatedFetch('/api/campaigns');
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      setCampaigns(data.campaigns || []);
    } catch (error) {
      console.error('Erro ao carregar campanhas:', error);
      toast.error('Erro ao carregar campanhas');
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch]);

  const loadContactTags = useCallback(async () => {
    try {
      const response = await authenticatedFetch('/api/campaigns/tags');
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const tags = await response.json();
      setContactTags(tags);
    } catch (error) {
      console.error('Erro ao carregar tags:', error);
    }
  }, [authenticatedFetch]);

  const loadWhatsAppSessions = useCallback(async () => {
    try {
      const response = await authenticatedFetch('/api/campaigns/sessions');
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const sessions = await response.json();
      setWhatsappSessions(sessions);
    } catch (error) {
      console.error('Erro ao carregar sessões:', error);
    }
  }, [authenticatedFetch]);

  useEffect(() => {
    if (selectedTenantId) {
      loadCampaigns();
      loadContactTags();
      loadWhatsAppSessions();
    }
  }, [selectedTenantId, loadCampaigns, loadContactTags, loadWhatsAppSessions]);

  useEffect(() => {
    if (!selectedTenantId) return;
    const hasActive = campaigns.some(
      c => c.status === 'PENDING' || c.status === 'RUNNING' || c.status === 'PAUSED'
    );
    if (!hasActive) return;
    const id = setInterval(() => {
      loadCampaigns();
    }, 30000);
    return () => clearInterval(id);
  }, [selectedTenantId, campaigns, loadCampaigns]);

  const handleTagToggle = (tag: string) => {
    setFormData(prev => ({
      ...prev,
      targetTags: prev.targetTags.includes(tag)
        ? prev.targetTags.filter(t => t !== tag)
        : [...prev.targetTags, tag]
    }));
  };

  const handleSessionToggle = (sessionName: string) => {
    setFormData(prev => ({
      ...prev,
      sessionNames: prev.sessionNames.includes(sessionName)
        ? prev.sessionNames.filter(s => s !== sessionName)
        : [...prev.sessionNames, sessionName]
    }));
  };

  const totalSelectedContacts = useMemo(() => {
    return formData.targetTags
      .map(tagId => contactTags.find(t => t.id === tagId)?.count || 0)
      .reduce((a, b) => a + b, 0);
  }, [formData.targetTags, contactTags]);

  const avgDelaySec = useMemo(() => {
    if (formData.randomDelay > 0) return formData.randomDelay / 2;
    return 0;
  }, [formData.randomDelay]);

  const etaTotalLabel = avgDelaySec > 0
    ? formatDuration(totalSelectedContacts * avgDelaySec)
    : 'Sem pausa configurada — envio imediato';

  const etaBatchLabel = avgDelaySec > 0 && formData.batchSize
    ? formatDuration(formData.batchSize * avgDelaySec)
    : '—';

  const handleCreateCampaign = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.targetTags.length === 0) {
      toast.error('Selecione pelo menos uma categoria de contatos');
      return;
    }

    if (formData.sessionNames.length === 0) {
      toast.error('Selecione pelo menos uma conexão WhatsApp');
      return;
    }

    if (!('sequence' in formData.messageContent) || formData.messageContent.sequence.length === 0) {
      toast.error('Adicione pelo menos uma mensagem à campanha');
      return;
    }

    try {
      const sequence = formData.messageContent.sequence;
      const isSequence = sequence.length > 1;

      let finalMessageType: string;
      let finalMessageContent: MessageContent;

      if (isSequence) {
        finalMessageType = 'sequence';
        finalMessageContent = { sequence };
      } else {
        const singleMessage = sequence[0];
        finalMessageType = singleMessage.type;
        finalMessageContent = singleMessage.content as MessageContent;
      }

      // Converter scheduledFor de datetime-local para ISO string
      let scheduledForISO = null;
      if (!formData.startImmediately && formData.scheduledFor) {
        // datetime-local retorna formato "YYYY-MM-DDTHH:mm" SEM timezone
        // Precisamos interpretar como horário LOCAL do usuário e converter para UTC
        // Exemplo: usuário escolhe 2025-10-15T19:00 em UTC-3 (Brasília)
        // Isso deve ser salvo como 2025-10-15T22:00:00.000Z (19:00 - 3 = 22:00 UTC)
        const localDateStr = formData.scheduledFor; // "2025-10-15T19:00"
        const localDate = new Date(localDateStr); // JavaScript interpreta como LOCAL
        scheduledForISO = localDate.toISOString(); // Converte para UTC com timezone
      }

      const campaignData = {
        nome: formData.nome,
        targetTags: formData.targetTags,
        sessionNames: formData.sessionNames,
        sessionName: formData.sessionName,
        messageType: finalMessageType,
        messageContent: finalMessageContent,
        deliveryMode: formData.deliveryMode,
        randomDelay: formData.randomDelay,
        startImmediately: formData.startImmediately,
        scheduledFor: scheduledForISO,
        batchSize: formData.batchSize,
        dailyCutoff: formData.dailyCutoff || null,
        deadlineAt: formData.deadlineAt ? new Date(formData.deadlineAt).toISOString() : null
      };

      const response = await authenticatedFetch('/api/campaigns', {
        method: 'POST',
        body: JSON.stringify(campaignData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.errors && errorData.errors.length > 0) {
          const validationErrors = errorData.errors.map((err: { msg: string }) => err.msg).join(', ');
          throw new Error(validationErrors);
        } else if (errorData.error) {
          throw new Error(errorData.error);
        } else {
          throw new Error(`HTTP ${response.status}`);
        }
      }

      toast.success('Campanha criada com sucesso!');
      setShowCreateModal(false);
      resetForm();
      loadCampaigns();
    } catch (error) {
      console.error('Erro ao criar campanha:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao criar campanha');
    }
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      targetTags: [],
      sessionNames: [],
      sessionName: '',
      messageType: 'sequence',
      messageContent: { sequence: [] },
      deliveryMode: 'SEQUENCE',
      randomDelay: 30,
      startImmediately: true,
      scheduledFor: '',
      batchSize: null,
      dailyCutoff: '',
      deadlineAt: ''
    });
    setUploadingFiles({});
    setFileInfos({});
    setDraggedIndex(null);
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
    const newSequence = [...currentSequence];
    const [draggedItem] = newSequence.splice(draggedIndex, 1);
    newSequence.splice(dropIndex, 0, draggedItem);

    setFormData(prev => ({
      ...prev,
      messageContent: { sequence: newSequence }
    }));
    setDraggedIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleFileUpload = async (file: File, messageIndex: number, variationIndex?: number) => {
    const uploadKey = variationIndex !== undefined ? `${messageIndex}-${variationIndex}` : messageIndex.toString();
    setUploadingFiles(prev => ({ ...prev, [uploadKey]: true }));

    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const token = localStorage.getItem('auth_token');
      const headers: HeadersInit = {};

      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch('/api/media/upload', {
        method: 'POST',
        body: uploadFormData,
        headers
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Erro ao fazer upload do arquivo');
      }

      const data = await response.json();

      // Armazenar informações do arquivo
      if (variationIndex !== undefined) {
        setFileInfos(prev => ({
          ...prev,
          [`${messageIndex}-${variationIndex}`]: {
            name: data.originalName,
            size: data.size,
            type: data.mimetype
          }
        }));
      } else {
        setFileInfos(prev => ({
          ...prev,
          [messageIndex]: {
            name: data.originalName,
            size: data.size,
            type: data.mimetype
          }
        }));
      }

      const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];

      if (variationIndex !== undefined) {
        // Upload para variação
        const newSequence = currentSequence.map((seqItem, i) => {
          if (i === messageIndex) {
            const content = seqItem.content as { mediaVariations?: Array<{ url: string; caption?: string; fileName?: string }> };
            const mediaVariations = [...(content.mediaVariations || [])];
            // Garantir que o array tenha o tamanho necessário
            while (mediaVariations.length <= variationIndex) {
              mediaVariations.push({ url: '', caption: '', fileName: '' });
            }
            mediaVariations[variationIndex] = {
              ...mediaVariations[variationIndex],
              url: data.fileUrl,
              fileName: data.originalName
            };
            return {
              ...seqItem,
              content: { ...content, mediaVariations }
            };
          }
          return seqItem;
        });

        setFormData(prev => ({
          ...prev,
          messageContent: { sequence: newSequence }
        }));
      } else {
        // Upload para modo single
        const newSequence = currentSequence.map((seqItem, i) =>
          i === messageIndex ? {
            ...seqItem,
            content: { ...seqItem.content, url: data.fileUrl }
          } : seqItem
        );

        setFormData(prev => ({
          ...prev,
          messageContent: { sequence: newSequence }
        }));
      }

      toast.success('Arquivo carregado com sucesso!');
    } catch (error) {
      console.error('Erro ao fazer upload:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao fazer upload do arquivo');
    } finally {
      setUploadingFiles(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  const handleRemoveFile = (messageIndex: number) => {
    const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
    const newSequence = currentSequence.map((seqItem, i) =>
      i === messageIndex ? {
        ...seqItem,
        content: { ...seqItem.content, url: '' }
      } : seqItem
    );

    setFormData(prev => ({
      ...prev,
      messageContent: { sequence: newSequence }
    }));

    setFileInfos(prev => {
      const updated = { ...prev };
      delete updated[messageIndex];
      return updated;
    });

    toast.success('Arquivo removido');
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };



  const handleToggleCampaign = async (campaignId: string, action: 'pause' | 'resume' | 'cancel') => {
    const executeAction = async () => {
      try {
        const response = await authenticatedFetch(`/api/campaigns/${campaignId}/toggle`, {
          method: 'PATCH',
          body: JSON.stringify({ action })
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const actionText = action === 'pause' ? 'pausada' : action === 'resume' ? 'retomada' : 'cancelada';
        toast.success(`Campanha ${actionText} com sucesso!`);
        loadCampaigns();
      } catch (error) {
        console.error('Erro ao alterar status da campanha:', error);
        toast.error('Erro ao alterar status da campanha');
      }
    };

    if (action === 'cancel') {
      showConfirm({
        title: 'Cancelar Campanha',
        message: 'Tem certeza que deseja cancelar esta campanha? O envio será interrompido permanentemente para esta campanha.',
        confirmText: 'Cancelar Campanha',
        type: 'danger',
        onConfirm: executeAction
      });
      return;
    }

    executeAction();
  };

  const handleDeleteCampaign = async (campaignId: string) => {
    showConfirm({
      title: 'Excluir Campanha',
      message: 'Tem certeza que deseja excluir esta campanha? Esta ação não pode ser desfeita.',
      confirmText: 'Excluir',
      type: 'danger',
      onConfirm: async () => {
        try {
          const response = await authenticatedFetch(`/api/campaigns/${campaignId}`, {
            method: 'DELETE'
          });

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }

          toast.success('Campanha excluída com sucesso!');
          loadCampaigns();
        } catch (error) {
          console.error('Erro ao excluir campanha:', error);
          toast.error('Erro ao excluir campanha');
        }
      }
    });
  };

  const handleDuplicateCampaign = (campaign: Campaign) => {
    let sequence: Array<{ type: string; content: MessageItemContent }> = [];

    if ('sequence' in campaign.messageContent && campaign.messageContent.sequence) {
      sequence = campaign.messageContent.sequence;
    } else if ('text' in campaign.messageContent) {
      sequence = [{ type: 'text', content: { text: campaign.messageContent.text } }];
    } else if ('url' in campaign.messageContent) {
      const content = campaign.messageContent as { url: string; caption?: string; fileName?: string };
      if (campaign.messageType === 'document') {
        sequence = [{ type: 'document', content: { url: content.url, fileName: content.fileName } }];
      } else {
        sequence = [{ type: campaign.messageType, content: { url: content.url, caption: content.caption } }];
      }
    }

    setFormData({
      nome: `${campaign.nome} (cópia)`,
      targetTags: campaign.targetTags,
      sessionNames: campaign.sessionNames,
      sessionName: campaign.sessionName,
      messageType: 'sequence',
      messageContent: { sequence },
      deliveryMode: campaign.deliveryMode || 'SEQUENCE',
      randomDelay: campaign.randomDelay,
      startImmediately: true,
      scheduledFor: '',
      batchSize: campaign.batchSize,
      dailyCutoff: campaign.dailyCutoff || '',
      deadlineAt: campaign.deadlineAt || ''
    });

    setUploadingFiles({});
    setFileInfos({});
    setDraggedIndex(null);
    setActiveTab('general');
    setShowCreateModal(true);
  };

  const handleViewReport = async (campaignId: string) => {
    setCurrentReportCampaignId(campaignId);
    setReportCurrentPage(1);
    setReportLoading(true);
    setShowReportModal(true);

    try {
      const response = await authenticatedFetch(`/api/campaigns/${campaignId}/report`);

      if (response.ok) {
        const data = await response.json();
        setReportData(data);
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Erro ao carregar relatório');
        setShowReportModal(false);
      }
    } catch (error) {
      console.error('Erro ao buscar relatório:', error);
      toast.error('Erro ao carregar relatório');
      setShowReportModal(false);
    } finally {
      setReportLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30';
      case 'RUNNING':
        return 'bg-blue-500/20 text-blue-300 border border-blue-500/30';
      case 'COMPLETED':
        return 'bg-green-500/20 text-green-300 border border-green-500/30';
      case 'PAUSED':
        return 'bg-orange-500/20 text-orange-300 border border-orange-500/30';
      case 'FAILED':
        return 'bg-red-500/20 text-red-300 border border-red-500/30';
      case 'CANCELED':
        return 'bg-gray-500/20 text-gray-300 border border-gray-500/30 line-through decoration-gray-400';
      default:
        return 'bg-gray-500/20 text-gray-300 border border-gray-500/30';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'Aguardando';
      case 'RUNNING':
        return 'Executando';
      case 'COMPLETED':
        return 'Concluída';
      case 'PAUSED':
        return 'Pausada';
      case 'FAILED':
        return 'Falha';
      case 'CANCELED':
        return 'Cancelada';
      default:
        return status;
    }
  };

  function pausedReasonLabel(reason: string | null): string | null {
    switch (reason) {
      case 'MANUAL': return 'Pausa manual';
      case 'BATCH_COMPLETED': return 'Batelada concluída';
      case 'DAILY_CUTOFF': return 'Horário máximo atingido';
      case 'DEADLINE_REACHED': return 'Deadline alcançado';
      default: return null;
    }
  }

  const handleRefreshReport = async () => {
    if (!currentReportCampaignId) return;

    setReportLoading(true);
    try {
      const response = await authenticatedFetch(`/api/campaigns/${currentReportCampaignId}/report`);

      if (response.ok) {
        const data = await response.json();
        setReportData(data);
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Erro ao carregar relatório');
      }
    } catch (error) {
      console.error('Erro ao buscar relatório:', error);
      toast.error('Erro ao carregar relatório');
    } finally {
      setReportLoading(false);
    }
  };

  const handleDownloadReport = async () => {
    if (!currentReportCampaignId || !reportData) return;

    try {
      const response = await authenticatedFetch(`/api/campaigns/${currentReportCampaignId}/report/download`);

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `relatorio-campanha-${reportData.campaign.nome}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Relatório baixado com sucesso!');
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Erro ao baixar relatório');
      }
    } catch (error) {
      console.error('Erro ao baixar relatório:', error);
      toast.error('Erro ao baixar relatório');
    }
  };

  const getErrorIcon = (errorMessage: string) => {
    if (!errorMessage || errorMessage === 'N/A') return null;

    const error = errorMessage.toLowerCase();

    if (error.includes('network') || error.includes('connection') || error.includes('timeout')) {
      return {
        icon: '🌐',
        color: 'text-orange-400',
        bgColor: 'bg-orange-500/20',
        category: 'Conectividade'
      };
    }

    if (error.includes('blocked') || error.includes('banned') || error.includes('spam')) {
      return {
        icon: '🚫',
        color: 'text-red-400',
        bgColor: 'bg-red-500/20',
        category: 'Bloqueio'
      };
    }

    if (error.includes('rate limit') || error.includes('quota') || error.includes('limit')) {
      return {
        icon: '⏰',
        color: 'text-yellow-400',
        bgColor: 'bg-yellow-500/20',
        category: 'Rate Limit'
      };
    }

    if (error.includes('invalid') || error.includes('not found') || error.includes('number')) {
      return {
        icon: '📱',
        color: 'text-blue-400',
        bgColor: 'bg-blue-500/20',
        category: 'Número Inválido'
      };
    }

    if (error.includes('permission') || error.includes('unauthorized') || error.includes('forbidden')) {
      return {
        icon: '🔒',
        color: 'text-purple-400',
        bgColor: 'bg-purple-500/20',
        category: 'Permissão'
      };
    }

    if (error.includes('session') || error.includes('disconnected') || error.includes('logout')) {
      return {
        icon: '📲',
        color: 'text-indigo-400',
        bgColor: 'bg-indigo-500/20',
        category: 'Sessão'
      };
    }

    if (error.includes('media') || error.includes('file') || error.includes('download')) {
      return {
        icon: '📁',
        color: 'text-gray-400',
        bgColor: 'bg-gray-500/20',
        category: 'Mídia'
      };
    }

    // Erro genérico
    return {
      icon: '⚠️',
      color: 'text-red-400',
      bgColor: 'bg-red-500/20',
      category: 'Erro Geral'
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] flex items-center justify-center p-8">
        <div className="flex flex-col items-center gap-6 bg-white/5 p-12 rounded-[2.5rem] border border-white/10 backdrop-blur-md shadow-2xl">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-8 h-8 bg-blue-500/10 rounded-full animate-pulse"></div>
            </div>
          </div>
          <span className="text-gray-400 font-oswald uppercase tracking-[0.2em] font-bold text-sm">Carregando campanhas...</span>
        </div>
      </div>
    );
  }

  const filteredCampaigns = campaigns.filter(campaign =>
    campaign.nome.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] p-4 font-oswald">
      <div className="max-w-7xl mx-auto space-y-4">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white/5 p-4 rounded-[2rem] border border-white/10 backdrop-blur-md shadow-2xl relative overflow-hidden group">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          <div className="relative z-10">
            <h1 className="text-4xl font-bold text-white tracking-tight uppercase">
              Campanhas
            </h1>
            <p className="text-gray-400 mt-2 font-medium tracking-wide uppercase text-xs opacity-80">
              Gerencie seus envios em massa e automações
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 relative z-10">
            <div className="relative group/search min-w-[300px]">
              <input
                type="text"
                placeholder="Pesquisar campanhas..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-black/40 border border-white/10 rounded-2xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all font-medium text-white placeholder-gray-500 backdrop-blur-xl"
              />
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 group-focus-within/search:text-blue-400 transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </span>
            </div>

            <button
              onClick={() => setShowCreateModal(true)}
              className="px-8 py-3 bg-blue-600 text-white rounded-2xl hover:shadow-[0_0_20px_rgba(37,99,235,0.3)] transition-all duration-300 font-bold uppercase tracking-[0.2em] text-xs flex items-center justify-center gap-3 border border-blue-500 relative overflow-hidden group/btn"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
              <span className="text-lg">+</span>
              NOVA CAMPANHA
            </button>
          </div>
        </div>

        {/* Lista de Campanhas */}
        <div className="bg-white/5 backdrop-blur-md rounded-[2.5rem] border border-white/10 shadow-2xl overflow-hidden">
          <div className="p-8 border-b border-white/10 bg-white/5">
            <h3 className="text-lg font-bold text-white tracking-[0.1em] flex items-center gap-3 uppercase">
              <span className="w-2 h-6 bg-blue-600 rounded-full shadow-[0_0_15px_rgba(37,99,235,0.5)]"></span>
              Últimas Campanhas
            </h3>
          </div>

          {filteredCampaigns.length === 0 ? (
            <div className="p-20 text-center flex flex-col items-center gap-6">
              <div className="w-24 h-24 bg-white/5 rounded-[2rem] flex items-center justify-center text-4xl shadow-inner border border-white/10 text-gray-600">
                📭
              </div>
              <div>
                <h4 className="font-bold text-white text-xl uppercase tracking-wider">Nenhuma campanha encontrada</h4>
                <p className="text-gray-400 mt-2 max-w-md mx-auto uppercase text-[10px] tracking-[0.2em] font-bold">
                  Crie sua primeira campanha para começar a engajar seus contatos.
                </p>
              </div>
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {filteredCampaigns.map((campaign) => (
                <div key={campaign.id} className="group px-8 py-6 hover:bg-white/[0.02] transition-all duration-300">
                  <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                    {/* Nome e Status */}
                    <div className="flex items-center gap-5 flex-1 w-full md:w-auto">
                      <div className="w-14 h-14 rounded-2xl bg-white/5 flex items-center justify-center text-2xl shadow-lg border border-white/10 group-hover:scale-110 group-hover:bg-blue-600/10 group-hover:border-blue-500/30 transition-all duration-500">
                        📢
                      </div>
                      <div className="min-w-0 flex-1">
                        <h4 className="text-xl font-bold text-white tracking-wide mb-2 truncate uppercase">
                          {campaign.nome}
                        </h4>
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.2em] border shadow-sm ${getStatusColor(campaign.status)}`}>
                          {getStatusText(campaign.status)}
                        </span>
                        {campaign.status === 'PAUSED' && pausedReasonLabel(campaign.pausedReason) && (
                          <span className="ml-2 text-[10px] bg-yellow-500/10 text-yellow-300 border border-yellow-500/30 px-2 py-0.5 rounded-full uppercase tracking-wider font-bold font-oswald">
                            {pausedReasonLabel(campaign.pausedReason)}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Informações essenciais */}
                    <div className="flex flex-wrap items-center gap-6 md:gap-10 text-sm w-full md:w-auto justify-start md:justify-end">
                      <div className="flex flex-col items-center md:items-end gap-1">
                        <span className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.2em]">Contatos</span>
                        <span className="font-bold text-white text-lg font-oswald tracking-wider">{campaign.totalContacts}</span>
                      </div>

                      <div className="flex flex-col items-center md:items-end gap-1">
                        <span className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.2em]">Enviados</span>
                        <span className="font-bold text-blue-400 text-lg font-oswald tracking-wider">{campaign.sentCount}</span>
                      </div>

                      {/* Sessões ativas */}
                      {campaign.sessionNames && campaign.sessionNames.length > 0 && (
                        <div className="flex flex-col items-center md:items-end gap-2 hidden lg:flex">
                          <span className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.2em]">Conexões</span>
                          <div className="flex -space-x-2">
                            {campaign.sessionNames.slice(0, 3).map((sessionName, index) => (
                              <div key={index} className="w-8 h-8 rounded-full bg-blue-900/40 border-2 border-[#0f172a] flex items-center justify-center text-[10px] font-bold text-blue-300 shadow-lg" title={sessionName}>
                                {sessionName.charAt(0).toUpperCase()}
                              </div>
                            ))}
                            {campaign.sessionNames.length > 3 && (
                              <div className="w-8 h-8 rounded-full bg-gray-800 border-2 border-[#0f172a] flex items-center justify-center text-[10px] font-bold text-gray-400 shadow-lg">
                                +{campaign.sessionNames.length - 3}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Botões de ação */}
                    <div className="flex items-center gap-3 w-full md:w-auto justify-end border-t md:border-t-0 pt-4 md:pt-0 border-white/5">
                      <SimpleTooltip content="Ver Relatório">
                        <button
                          onClick={() => handleViewReport(campaign.id)}
                          className="p-3 text-blue-400 bg-blue-500/10 hover:bg-blue-600 hover:text-white rounded-xl transition-all duration-300 border border-blue-500/20 hover:border-blue-500 shadow-lg hover:shadow-blue-500/20"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                        </button>
                      </SimpleTooltip>

                      <SimpleTooltip content="Duplicar Campanha">
                        <button
                          onClick={() => handleDuplicateCampaign(campaign)}
                          className="p-3 text-purple-400 bg-purple-500/10 hover:bg-purple-600 hover:text-white rounded-xl transition-all duration-300 border border-purple-500/20 hover:border-purple-500 shadow-lg hover:shadow-purple-500/20"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </button>
                      </SimpleTooltip>

                      {/* Botão Cancelar */}
                      {['PENDING', 'RUNNING', 'PAUSED'].includes(campaign.status) && (
                        <SimpleTooltip content="Cancelar Campanha">
                          <button
                            onClick={() => handleToggleCampaign(campaign.id, 'cancel')}
                            className="p-3 text-red-400 bg-red-500/10 hover:bg-red-600 hover:text-white rounded-xl transition-all duration-300 border border-red-500/20 hover:border-red-500 shadow-lg hover:shadow-red-500/20"
                          >
                            <XCircle className="w-5 h-5" />
                          </button>
                        </SimpleTooltip>
                      )}

                      <SimpleTooltip content="Excluir">
                        <button
                          onClick={() => handleDeleteCampaign(campaign.id)}
                          className="p-3 text-red-400 bg-red-500/10 hover:bg-red-600 hover:text-white rounded-xl transition-all duration-300 border border-red-500/20 hover:border-red-500 shadow-lg hover:shadow-red-500/20"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </SimpleTooltip>

                      {campaign.status === 'RUNNING' && (
                        <SimpleTooltip content="Pausar">
                          <button
                            onClick={() => handleToggleCampaign(campaign.id, 'pause')}
                            className="p-3 text-yellow-400 bg-yellow-500/10 hover:bg-yellow-600 hover:text-black rounded-xl transition-all duration-300 border border-yellow-500/20 hover:border-yellow-500 shadow-lg hover:shadow-yellow-500/20"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </button>
                        </SimpleTooltip>
                      )}

                      {campaign.status === 'PAUSED' && (
                        <SimpleTooltip content="Retomar">
                          <button
                            onClick={() => handleToggleCampaign(campaign.id, 'resume')}
                            className="p-3 text-green-400 bg-green-500/10 hover:bg-green-600 hover:text-white rounded-xl transition-all duration-300 border border-green-500/20 hover:border-green-500 shadow-lg hover:shadow-green-500/20"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </button>
                        </SimpleTooltip>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modal de Criação */}
      {showCreateModal && (
        <Portal>
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 font-oswald">
            <div
              className="absolute inset-0 bg-black/80 backdrop-blur-md transition-opacity duration-300"
              onClick={() => setShowCreateModal(false)}
            />

            <div className="relative w-full max-w-[76rem] bg-gradient-to-b from-[#1e3a5f] to-black backdrop-blur-2xl rounded-[2rem] shadow-2xl border border-white/20 flex flex-col max-h-[99vh] overflow-hidden animate-scaleIn">
              {/* Header Premium */}
              <div className="shrink-0 px-8 py-6 border-b border-white/10 flex items-center justify-between bg-white/5 backdrop-blur-md">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-700 rounded-2xl flex items-center justify-center shadow-lg transform rotate-3 hover:rotate-6 transition-transform duration-300">
                    <span className="text-2xl">🚀</span>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white font-oswald tracking-wide uppercase">
                      Nova Campanha
                    </h2>
                    <p className="text-sm text-gray-400 font-medium tracking-wide opacity-80">
                      Crie e configure sua campanha de mensagens
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="group p-2 rounded-full hover:bg-white/10 transition-colors duration-200"
                >
                  <div className="w-8 h-8 rounded-full border-2 border-white/20 group-hover:border-red-500/50 flex items-center justify-center transition-colors">
                    <svg className="w-4 h-4 text-gray-400 group-hover:text-red-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                </button>
              </div>

              {/* Tab Navigation */}
              <div className="shrink-0 px-8 py-4 bg-black/20 border-b border-white/10">
                <div className="flex gap-3">
                  <button type="button" onClick={() => setActiveTab('general')} className={`flex-1 py-3 px-4 rounded-xl text-sm font-bold font-oswald uppercase tracking-wider transition-all ${activeTab === 'general' ? 'bg-blue-600 text-white shadow-lg' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}>
                    1. Dados da Campanha
                  </button>
                  <button type="button" onClick={() => setActiveTab('parameters')} className={`flex-1 py-3 px-4 rounded-xl text-sm font-bold font-oswald uppercase tracking-wider transition-all ${activeTab === 'parameters' ? 'bg-blue-600 text-white shadow-lg' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}>
                    2. Parâmetros
                  </button>
                   <button type="button" onClick={() => setActiveTab('messages')} className={`flex-1 py-3 px-4 rounded-xl text-sm font-bold font-oswald uppercase tracking-wider transition-all ${activeTab === 'messages' ? 'bg-blue-600 text-white shadow-lg' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}>
                     3. Mensagens
                   </button>
                   <button type="button" onClick={() => setActiveTab('summary')} className={`flex-1 py-3 px-4 rounded-xl text-sm font-bold font-oswald uppercase tracking-wider transition-all ${activeTab === 'summary' ? 'bg-blue-600 text-white shadow-lg' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}>
                     4. Resumo
                   </button>
                 </div>
              </div>

              {/* Form Content */}
              <div className="flex-1 overflow-y-auto custom-scrollbar">
                <form onSubmit={handleCreateCampaign} className="p-4 space-y-4">

                  {activeTab === 'general' && (
                    <>
                      {/* Basic Info Section */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        <div className="space-y-6">
                          <div className="bg-white/5 rounded-2xl p-6 border border-white/10 shadow-sm hover:shadow-md transition-shadow duration-300">
                            <label className="block text-sm font-bold text-gray-300 mb-2 uppercase tracking-wide font-oswald">
                              Nome da Campanha
                            </label>
                            <input
                              type="text"
                              required
                              value={formData.nome}
                              onChange={(e) => setFormData(prev => ({ ...prev, nome: e.target.value }))}
                              className="w-full px-4 py-3 bg-black/20 border border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all font-medium text-white placeholder-gray-500"
                              placeholder="Ex: Promoção de Natal"
                            />
                          </div>

                          <div className="bg-white/5 rounded-2xl p-6 border border-white/10 shadow-sm hover:shadow-md transition-shadow duration-300">
                            <label className="block text-sm font-bold text-gray-300 mb-2 uppercase tracking-wide font-oswald">
                              Conexões WhatsApp
                            </label>
                            <div className="space-y-3 max-h-48 overflow-y-auto custom-scrollbar pr-2">
                              {whatsappSessions.length === 0 ? (
                                <p className="text-sm text-gray-500 italic">Nenhuma conexão disponível</p>
                              ) : (
                                whatsappSessions.map(session => (
                                  <label key={session.name} className="flex items-center gap-3 p-3 rounded-xl bg-black/20 hover:bg-white/5 border border-transparent hover:border-white/10 transition-all cursor-pointer group">
                                    <div className="relative flex items-center justify-center w-5 h-5">
                                      <input
                                        type="checkbox"
                                        checked={formData.sessionNames.includes(session.name)}
                                        onChange={() => handleSessionToggle(session.name)}
                                        className="peer appearance-none w-5 h-5 border-2 border-white/20 rounded-md checked:bg-blue-500 checked:border-blue-500 transition-colors bg-white/5"
                                      />
                                      <svg className="w-3.5 h-3.5 text-white absolute opacity-0 peer-checked:opacity-100 pointer-events-none transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path></svg>
                                    </div>
                                    <div className="flex-1">
                                      <span className="block text-sm font-medium text-gray-300 group-hover:text-white">
                                        {session.mePushName || session.displayName || session.name}
                                      </span>
                                      <span className="text-xs text-gray-500 font-mono">{session.name}</span>
                                    </div>
                                    <div className="w-2 h-2 rounded-full bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.4)]"></div>
                                  </label>
                                ))
                              )}
                            </div>
                            {formData.sessionNames.length > 0 && (
                              <div className="px-3 py-2 bg-blue-500/10 rounded-lg border border-blue-500/20 text-xs text-blue-300 font-medium flex items-center gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                                {formData.sessionNames.length} conexões selecionadas (Load Balancing Ativo)
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="space-y-6">
                          <div className="bg-white/5 rounded-2xl p-6 border border-white/10 shadow-sm hover:shadow-md transition-shadow duration-300">
                            <label className="block text-sm font-bold text-gray-300 mb-2 uppercase tracking-wide font-oswald">
                              Categorias de Contatos
                            </label>
                            <div className="space-y-3 max-h-48 overflow-y-auto custom-scrollbar pr-2">
                              {contactTags.length === 0 ? (
                                <p className="text-sm text-gray-500 italic">Nenhuma categoria encontrada</p>
                              ) : (
                                contactTags.map(tag => (
                                  <label key={tag.id} className="flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 border border-transparent hover:border-white/20 transition-all cursor-pointer group">
                                    <div className="relative flex items-center justify-center w-5 h-5">
                                      <input
                                        type="checkbox"
                                        checked={formData.targetTags.includes(tag.id)}
                                        onChange={() => handleTagToggle(tag.id)}
                                        className="peer appearance-none w-5 h-5 border-2 border-white/20 rounded-md checked:bg-blue-600 checked:border-blue-600 transition-colors bg-white/5"
                                      />
                                      <svg className="w-3.5 h-3.5 text-white absolute opacity-0 peer-checked:opacity-100 pointer-events-none transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path></svg>
                                    </div>
                                    <span className="text-sm font-medium text-gray-300 group-hover:text-white transition-colors">{tag.nome}</span>
                                  </label>
                                ))
                              )}
                            </div>
                          </div>

                          <div className="bg-white/5 rounded-2xl p-6 border border-white/10 shadow-sm hover:shadow-md transition-shadow duration-300">
                            <label className="block text-sm font-bold text-gray-300 mb-2 uppercase tracking-wide font-oswald">
                              Agendamento
                            </label>
                            <div className="space-y-4">
                              <div className="flex gap-4 p-1 bg-black/20 rounded-xl border border-white/10">
                                <button
                                  type="button"
                                  onClick={() => setFormData(prev => ({ ...prev, startImmediately: true }))}
                                  className={`flex-1 py-2 text-sm font-bold rounded-lg transition-all ${formData.startImmediately
                                    ? 'bg-blue-600 text-white shadow-lg'
                                    : 'text-gray-400 hover:text-white'
                                    }`}
                                >
                                  🚀 Imediato
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setFormData(prev => ({ ...prev, startImmediately: false }))}
                                  className={`flex-1 py-2 text-sm font-bold rounded-lg transition-all ${!formData.startImmediately
                                    ? 'bg-blue-600 text-white shadow-lg'
                                    : 'text-gray-400 hover:text-white'
                                    }`}
                                >
                                  📅 Agendar
                                </button>
                              </div>

                              {!formData.startImmediately && (
                                <input
                                  type="datetime-local"
                                  required={!formData.startImmediately}
                                  value={formData.scheduledFor || ''}
                                  onChange={(e) => setFormData(prev => ({ ...prev, scheduledFor: e.target.value }))}
                                  className="w-full px-4 py-3 bg-black/20 border border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all font-medium text-white"
                                />
                              )}
                            </div>
                          </div>

                        </div>
                      </div>
                    </>
                  )}

                  {activeTab === 'parameters' && (
                    <>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                        {/* Card Pausa Aleatória (movido de general) */}
                        <div className="bg-white/5 rounded-2xl p-6 border border-white/10 shadow-sm hover:shadow-md transition-shadow duration-300">
                          <label className="block text-sm font-bold text-gray-300 mb-2 uppercase tracking-wide font-oswald">
                            Pausa aleatória entre envios (segundos)
                          </label>
                          <input
                            type="number"
                            min="0"
                            value={formData.randomDelay}
                            onChange={(e) => setFormData(prev => ({ ...prev, randomDelay: parseInt(e.target.value) || 0 }))}
                            className="w-full px-4 py-2.5 bg-black/20 border border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all font-medium text-white"
                          />
                          <p className="text-xs text-gray-400 mt-2">
                            Valor máximo aleatório em segundos aplicado antes de cada envio.
                          </p>
                        </div>

                        {/* Card Envio Cadenciado (novo) */}
                        <div className="bg-white/5 rounded-2xl border border-white/10 p-6 backdrop-blur-md">
                        <h3 className="text-lg font-bold text-white font-oswald tracking-wide uppercase mb-4">
                          🚀 Envio Cadenciado
                        </h3>
                        <div className="space-y-3 text-sm text-gray-300">
                          <div>Total de contatos: <span className="text-white font-bold">{totalSelectedContacts}</span></div>
                          <div>Tempo médio para enviar tudo: <span className="text-white font-bold">{etaTotalLabel}</span></div>
                        </div>
                        <label className="block mt-6 text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">
                          Enviar quantos agora? (em branco = enviar tudo)
                        </label>
                        <input
                          type="number"
                          min={1}
                          value={formData.batchSize ?? ''}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            batchSize: e.target.value === '' ? null : Math.max(1, Number(e.target.value))
                          }))}
                          placeholder="ex.: 50"
                          className="mt-2 w-full px-4 py-2.5 bg-black/40 border border-white/10 rounded-lg text-white focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 font-bold font-oswald text-lg"
                        />
                        {formData.batchSize != null && (
                          <p className="mt-3 text-xs text-blue-300">
                            Estimativa para {formData.batchSize} mensagens: <span className="font-bold">{etaBatchLabel}</span>.
                            Os outros {Math.max(0, totalSelectedContacts - formData.batchSize)} ficam em pausa.
                          </p>
                        )}
                      </div>
                      </div>

                      {/* Card Janelas de Tempo (novo) */}
                      <div className="bg-white/5 rounded-2xl border border-white/10 p-6 backdrop-blur-md mt-1.5">
                        <h3 className="text-lg font-bold text-white font-oswald tracking-wide uppercase mb-4">
                          🕐 Janelas de Tempo
                        </h3>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-1.5">
                          <div>
                            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">
                              Horário máximo diário (opcional)
                            </label>
                            <input
                              type="time"
                              value={formData.dailyCutoff}
                              onChange={(e) => setFormData(prev => ({ ...prev, dailyCutoff: e.target.value }))}
                              className="mt-2 w-full px-4 py-2.5 bg-black/40 border border-white/10 rounded-lg text-white focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 font-bold font-oswald"
                            />
                            <p className="text-xs text-gray-400 mt-2">
                              Quando o relógio passar deste horário (fuso America/Recife), a campanha pausa.
                              Retomada manual no dia seguinte respeita o mesmo limite.
                            </p>
                          </div>

                          <div>
                            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">
                              Deadline absoluto (opcional)
                            </label>
                            <input
                              type="datetime-local"
                              value={formData.deadlineAt}
                              onChange={(e) => setFormData(prev => ({ ...prev, deadlineAt: e.target.value }))}
                              className="mt-2 w-full px-4 py-2.5 bg-black/40 border border-white/10 rounded-lg text-white focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 font-bold font-oswald"
                            />
                            <p className="text-xs text-gray-400 mt-2">
                              Após esta data/hora, a campanha não é mais executada.
                            </p>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {activeTab === 'messages' && (
                    <>
                      {/* Delivery Mode Selector */}
                      <div className="border-t border-white/10 pt-8">
                        <div className="flex items-center justify-between mb-6">
                          <div className="flex items-center gap-2">
                            <h3 className="text-xl font-bold text-white font-oswald tracking-wide uppercase">
                              Modo de Entrega
                            </h3>
                            <div className="relative group">
                              <button type="button" className="w-5 h-5 rounded-full bg-blue-600/20 border border-blue-500/30 text-blue-400 text-[11px] font-bold flex items-center justify-center hover:bg-blue-600 hover:text-white transition-all cursor-help font-oswald">
                                i
                              </button>
                              <div className="absolute left-0 bottom-full mb-2 w-64 bg-gray-900 text-gray-200 text-xs rounded-xl p-3 opacity-0 group-hover:opacity-100 transition-all pointer-events-none z-10 border border-white/10 shadow-2xl font-medium leading-relaxed">
                                Escolha como as mensagens serão enviadas aos contatos
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="bg-black/40 rounded-2xl p-1 border border-white/10">
                              <div className="flex gap-1">
                              <button
                                type="button"
                                onClick={() => setFormData(prev => ({ ...prev, deliveryMode: 'SEQUENCE' }))}
                                className={`relative group px-4 py-2 rounded-xl text-sm font-bold font-oswald uppercase tracking-wider transition-all ${
                                  formData.deliveryMode === 'SEQUENCE'
                                    ? 'bg-blue-600 text-white shadow-lg'
                                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                                }`}
                                >
                                  Sequência
                                  <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-64 bg-gray-900 text-gray-200 text-xs rounded-xl p-3 opacity-0 group-hover:opacity-100 transition-all pointer-events-none z-10 border border-white/10 shadow-2xl font-medium leading-relaxed whitespace-normal">
                                    Sequência: todas as mensagens serão enviadas em sequência para cada contato.
                                  </div>
                                </button>
                              <button
                                type="button"
                                onClick={() => setFormData(prev => ({ ...prev, deliveryMode: 'VARIATIONS' }))}
                                className={`relative group px-4 py-2 rounded-xl text-sm font-bold font-oswald uppercase tracking-wider transition-all ${
                                  formData.deliveryMode === 'VARIATIONS'
                                    ? 'bg-blue-600 text-white shadow-lg'
                                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                                }`}
                                >
                                  Variações
                                  <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-64 bg-gray-900 text-gray-200 text-xs rounded-xl p-3 opacity-0 group-hover:opacity-100 transition-all pointer-events-none z-10 border border-white/10 shadow-2xl font-medium leading-relaxed whitespace-normal">
                                    Variações: cada contato receberá apenas uma mensagem, alternando entre as variações cadastradas.
                                  </div>
                                </button>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                setFormData(prev => ({
                                  ...prev,
                                  messageContent: {
                                    sequence: [...currentSequence, { type: 'text', content: { text: '' } }]
                                  }
                                }));
                              }}
                              className="px-4 py-2 bg-blue-600 text-white rounded-xl text-xs font-bold hover:bg-blue-500 transition-all shadow-lg shadow-blue-900/20 uppercase tracking-[0.2em] flex items-center gap-2 font-oswald border border-blue-500 relative overflow-hidden group/btn whitespace-nowrap"
                            >
                              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
                              <span className="text-base">+</span> Adicionar Mensagem
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-4">
                          {('sequence' in formData.messageContent ? formData.messageContent.sequence : []).map((item, index) => (
                            <div key={index}
                              className={`group relative bg-white/5 rounded-2xl border transition-all duration-300 overflow-hidden backdrop-blur-md ${draggedIndex === index ? 'border-blue-500 shadow-[0_0_20px_rgba(37,99,235,0.2)] ring-2 ring-blue-500/20' : 'border-white/10 hover:border-white/20 shadow-sm'
                                }`}
                              draggable
                              onDragStart={() => handleDragStart(index)}
                              onDragOver={(e) => handleDragOver(e, index)}
                              onDrop={(e) => handleDrop(e, index)}
                              onDragEnd={handleDragEnd}
                            >
                              <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-500 to-blue-700" />

                              <div className="p-6 pl-8">
                                <div className="flex justify-between items-start mb-4">
                                  <div className="flex items-center gap-3">
                                    <div className="cursor-grab active:cursor-grabbing p-1.5 rounded-lg hover:bg-white/10 text-gray-400 hover:text-blue-400 transition-colors">
                                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" /></svg>
                                    </div>
                                    <span className="w-7 h-7 rounded-lg bg-blue-600/20 flex items-center justify-center text-xs font-bold text-blue-400 border border-blue-500/30 font-oswald shadow-[0_0_10px_rgba(37,99,235,0.2)]">
                                      {index + 1}
                                    </span>
                                    <div className="relative group/select">
                                      <select
                                        value={item.type}
                                        onChange={(e) => {
                                          const newType = e.target.value;
                                          let newContent: MessageItemContent = { text: '' };

                                          switch (newType) {
                                            case 'text': newContent = { text: '' }; break;
                                            case 'image':
                                            case 'video':
                                            case 'audio':
                                              newContent = { url: '', caption: '' }; break;
                                            case 'document': newContent = { url: '', fileName: '' }; break;
                                            case 'wait': newContent = { waitTime: 30 }; break;
                                            case 'openai': newContent = { model: 'gpt-3.5-turbo', system: '', user: '' }; break;
                                            case 'groq': newContent = { model: 'llama3-70b-8192', system: '', user: '' }; break;
                                          }

                                          const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                          const newSequence = currentSequence.map((seqItem, i) =>
                                            i === index ? { type: newType, content: newContent } : seqItem
                                          );
                                          setFormData(prev => ({ ...prev, messageContent: { sequence: newSequence } }));
                                        }}
                                        className="px-4 py-1.5 bg-black/40 border border-white/10 rounded-lg text-sm font-bold text-white focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 cursor-pointer hover:bg-white/5 transition-all appearance-none pr-10 font-oswald uppercase tracking-wider"
                                      >
                                        <option value="text" className="bg-[#1e3a5f]">💬 Texto</option>
                                        <option value="image" className="bg-[#1e3a5f]">🖼️ Imagem</option>
                                        <option value="video" className="bg-[#1e3a5f]">🎬 Vídeo</option>
                                        <option value="audio" className="bg-[#1e3a5f]">🎵 Áudio</option>
                                        <option value="document" className="bg-[#1e3a5f]">📄 Arquivo</option>
                                        <option value="openai" className="bg-[#1e3a5f]">🤖 OpenAI</option>
                                        <option value="groq" className="bg-[#1e3a5f]">⚡ Groq AI</option>
                                        <option value="wait" className="bg-[#1e3a5f]">⏱️ Espera</option>
                                      </select>
                                      <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 group-hover/select:text-blue-400 transition-colors">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                                      </div>
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                      const newSequence = currentSequence.filter((_, i) => i !== index);
                                      setFormData(prev => ({ ...prev, messageContent: { sequence: newSequence } }));
                                    }}
                                    className="text-gray-400 hover:text-red-500 transition-all p-2 hover:bg-red-500/10 rounded-xl border border-transparent hover:border-red-500/20"
                                  >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                  </button>
                                </div>

                                {/* Content Fields based on Type */}
                                <div className="space-y-4">
                                  {item.type === 'text' && (
                                    <div className="relative">
                                      <textarea
                                        value={(item.content as { text: string }).text || ''}
                                        onChange={(e) => {
                                          const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                          const newSequence = currentSequence.map((seqItem, i) =>
                                            i === index ? { ...seqItem, content: { ...seqItem.content, text: e.target.value } } : seqItem
                                          );
                                          setFormData(prev => ({ ...prev, messageContent: { sequence: newSequence } }));
                                        }}
                                        placeholder="Digite sua mensagem... Use variáveis como {{nome}}, {{telefone}}"
                                        rows={4}
                                        className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all font-medium text-white resize-none placeholder-gray-600 shadow-inner"
                                      />
                                      <div className="flex gap-2 mt-3 flex-wrap">
                                        {['{{nome}}', '{{telefone}}', '{{email}}', '{{categoria}}'].map(variable => (
                                          <button
                                            key={variable}
                                            type="button"
                                            onClick={() => {
                                              const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                              const currentText = (currentSequence[index]?.content as { text?: string })?.text || '';
                                              const newSequence = currentSequence.map((seqItem, i) =>
                                                i === index ? { ...seqItem, content: { ...seqItem.content, text: currentText + variable } } : seqItem
                                              );
                                              setFormData(prev => ({ ...prev, messageContent: { sequence: newSequence } }));
                                            }}
                                            className="text-[10px] bg-blue-600/10 border border-blue-500/30 px-3 py-1.5 rounded-lg text-blue-400 hover:bg-blue-600 hover:text-white hover:border-blue-500 transition-all font-bold uppercase tracking-[0.2em] font-oswald relative overflow-hidden group/btn"
                                          >
                                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
                                            {variable}
                                          </button>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {['image', 'video', 'audio', 'document'].includes(item.type) && (
                                    <div className="bg-black/40 rounded-xl p-8 border-2 border-dashed border-white/10 hover:border-blue-500/50 transition-all duration-300 text-center group relative overflow-hidden">
                                      <div className="absolute inset-0 bg-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                                      {!(item.content as { url?: string }).url ? (
                                        <>
                                          <input
                                            type="file"
                                            id={`file-${index}`}
                                            className="hidden"
                                            onChange={(e) => {
                                              const file = e.target.files?.[0];
                                              if (file) handleFileUpload(file, index);
                                            }}
                                            disabled={uploadingFiles[index]}
                                          />
                                          <label htmlFor={`file-${index}`} className="cursor-pointer flex flex-col items-center justify-center gap-4 relative z-10">
                                            {uploadingFiles[index] ? (
                                              <div className="relative">
                                                <div className="animate-spin w-12 h-12 border-4 border-blue-500/20 border-t-blue-500 rounded-full" />
                                                <div className="absolute inset-0 flex items-center justify-center">
                                                  <span className="text-[10px] font-bold text-blue-400 animate-pulse">...</span>
                                                </div>
                                              </div>
                                            ) : (
                                              <div className="w-16 h-16 bg-white/5 text-blue-400 rounded-2xl flex items-center justify-center shadow-lg border border-white/10 group-hover:scale-110 group-hover:bg-blue-600 group-hover:text-white group-hover:border-blue-500 transition-all duration-300">
                                                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                                </svg>
                                              </div>
                                            )}
                                            <div>
                                              <p className="text-sm font-bold text-white font-oswald uppercase tracking-wider">
                                                {uploadingFiles[index] ? 'Enviando arquivo...' : 'Clique para fazer upload'}
                                              </p>
                                              <p className="text-[10px] text-gray-400 mt-1.5 uppercase tracking-[0.2em] font-medium">
                                                {item.type === 'image' ? 'JPG, PNG, GIF' : item.type === 'video' ? 'MP4, AVI' : item.type === 'audio' ? 'MP3, OGG' : 'PDF, DOC, XLS'}
                                              </p>
                                            </div>
                                          </label>
                                        </>
                                      ) : (
                                        <div className="flex items-center justify-between bg-black/60 p-4 rounded-xl border border-white/10 shadow-xl backdrop-blur-xl relative z-10">
                                          <div className="flex items-center gap-4 overflow-hidden">
                                            {item.type === 'image' ? (
                                              <div className="relative group/preview">
                                                <img src={(item.content as { url: string }).url} alt="Preview" className="w-14 h-14 rounded-lg object-cover border border-white/10 shadow-lg group-hover:scale-105 transition-transform" />
                                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/preview:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                                                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                                                </div>
                                              </div>
                                            ) : (
                                              <div className="w-14 h-14 bg-white/5 rounded-lg flex items-center justify-center border border-white/10 shadow-lg">
                                                <span className="text-3xl">
                                                  {item.type === 'video' ? '🎬' : item.type === 'audio' ? '🎵' : '📄'}
                                                </span>
                                              </div>
                                            )}
                                            <div className="flex flex-col items-start min-w-0">
                                              <span className="text-sm font-bold text-white truncate max-w-[250px] font-oswald uppercase tracking-wide">
                                                {fileInfos[index]?.name || 'Arquivo processado'}
                                              </span>
                                              <div className="flex items-center gap-2 mt-1.5">
                                                <span className="text-[10px] text-blue-400 bg-blue-400/10 px-2.5 py-0.5 rounded-full font-bold uppercase tracking-[0.2em] border border-blue-400/20">
                                                  {fileInfos[index]?.size ? formatFileSize(fileInfos[index].size) : 'Pronto'}
                                                </span>
                                                <span className="text-[10px] text-green-400 bg-green-400/10 px-2.5 py-0.5 rounded-full font-bold uppercase tracking-[0.2em] border border-green-400/20">
                                                  Upload Concluído
                                                </span>
                                              </div>
                                            </div>
                                          </div>
                                          <button
                                            type="button"
                                            onClick={() => handleRemoveFile(index)}
                                            className="p-2.5 text-gray-400 hover:text-red-400 rounded-xl hover:bg-red-400/10 transition-all border border-transparent hover:border-red-500/20"
                                          >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  {['image', 'video', 'audio'].includes(item.type) && (item.content as { url?: string }).url && (
                                    <div className="relative">
                                      <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] mb-2 font-oswald">
                                        Legenda (opcional)
                                      </label>
                                      <textarea
                                        value={(item.content as { caption?: string }).caption || ''}
                                        onChange={(e) => {
                                          const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                          const newSequence = currentSequence.map((seqItem, i) =>
                                            i === index ? { ...seqItem, content: { ...seqItem.content, caption: e.target.value } } : seqItem
                                          );
                                          setFormData(prev => ({ ...prev, messageContent: { sequence: newSequence } }));
                                        }}
                                        placeholder="Texto que acompanha a mídia. Use variáveis como {{nome}}, {{telefone}}"
                                        rows={3}
                                        className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all font-medium text-white resize-none placeholder-gray-600 shadow-inner"
                                      />
                                      <div className="flex gap-2 mt-3 flex-wrap">
                                        {['{{nome}}', '{{telefone}}', '{{email}}', '{{categoria}}'].map(variable => (
                                          <button
                                            key={variable}
                                            type="button"
                                            onClick={() => {
                                              const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                              const currentCaption = (currentSequence[index]?.content as { caption?: string })?.caption || '';
                                              const newSequence = currentSequence.map((seqItem, i) =>
                                                i === index ? { ...seqItem, content: { ...seqItem.content, caption: currentCaption + variable } } : seqItem
                                              );
                                              setFormData(prev => ({ ...prev, messageContent: { sequence: newSequence } }));
                                            }}
                                            className="text-[10px] bg-blue-600/10 border border-blue-500/30 px-3 py-1.5 rounded-lg text-blue-400 hover:bg-blue-600 hover:text-white hover:border-blue-500 transition-all font-bold uppercase tracking-[0.2em] font-oswald relative overflow-hidden group/btn"
                                          >
                                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
                                            {variable}
                                          </button>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {item.type === 'wait' && (
                                    <div className="flex items-center gap-6 bg-black/40 rounded-xl p-5 border border-white/10 shadow-lg">
                                      <div className="w-12 h-12 rounded-2xl bg-blue-600 flex items-center justify-center text-white shadow-lg shadow-blue-900/20 border border-blue-500/50">
                                        <span className="text-2xl">⏱️</span>
                                      </div>
                                      <div className="flex-1">
                                        <label className="block text-xs font-bold text-gray-400 mb-2 uppercase tracking-[0.2em] font-oswald">
                                          Tempo de espera (segundos)
                                        </label>
                                        <div className="relative max-w-[200px]">
                                          <input
                                            type="number"
                                            min="1"
                                            max="3600"
                                            value={(item.content as { waitTime: number }).waitTime || 30}
                                            onChange={(e) => {
                                              const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                              const newSequence = currentSequence.map((seqItem, i) =>
                                                i === index ? { ...seqItem, content: { waitTime: parseInt(e.target.value) || 30 } } : seqItem
                                              );
                                              setFormData(prev => ({ ...prev, messageContent: { sequence: newSequence } }));
                                            }}
                                            className="w-full px-4 py-2.5 bg-black/40 border border-white/10 rounded-lg text-white focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 font-bold font-oswald text-lg"
                                          />
                                          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold text-blue-400 uppercase tracking-[0.2em] pointer-events-none">
                                            SEG
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {item.type === 'openai' && (
                                    <div className="space-y-4 animate-fadeIn">
                                      <div className="bg-black/40 rounded-xl p-6 border border-white/10 shadow-xl backdrop-blur-md">
                                        <div className="flex items-center gap-4 mb-6">
                                          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-700 rounded-2xl flex items-center justify-center text-white shadow-lg border border-white/10 transform hover:rotate-12 transition-transform">
                                            <span className="text-2xl">🤖</span>
                                          </div>
                                          <div>
                                            <h4 className="font-bold text-white font-oswald text-lg uppercase tracking-wide">Configuração OpenAI</h4>
                                            <p className="text-[10px] text-blue-400 font-bold uppercase tracking-[0.2em]">Geração de texto inteligente com GPT</p>
                                          </div>
                                        </div>

                                        <div className="space-y-5">
                                          <div>
                                            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] mb-2 font-oswald">Modelo</label>
                                            <div className="relative group/select">
                                              <select
                                                value={(item.content as { model: string }).model || 'gpt-3.5-turbo'}
                                                onChange={(e) => {
                                                  const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                                  const newSequence = currentSequence.map((seqItem, i) =>
                                                    i === index ? { ...seqItem, content: { ...seqItem.content, model: e.target.value } } : seqItem
                                                  );
                                                  setFormData(prev => ({ ...prev, messageContent: { sequence: newSequence } }));
                                                }}
                                                className="w-full px-4 py-2.5 bg-black/40 border border-white/10 rounded-lg text-sm font-bold focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all text-white appearance-none pr-10 font-oswald uppercase tracking-wider cursor-pointer"
                                              >
                                                <option value="gpt-3.5-turbo" className="bg-[#1e3a5f]">GPT-3.5 Turbo (Rápido & Econômico)</option>
                                                <option value="gpt-4" className="bg-[#1e3a5f]">GPT-4 (Maior precisão)</option>
                                                <option value="gpt-4-turbo" className="bg-[#1e3a5f]">GPT-4 Turbo</option>
                                                <option value="gpt-4o" className="bg-[#1e3a5f]">GPT-4o (Mais recente)</option>
                                              </select>
                                              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 group-hover/select:text-blue-400 transition-colors">
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                                              </div>
                                            </div>
                                          </div>

                                          <div>
                                            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] mb-2 font-oswald">
                                              Instrução do Sistema (System Prompt)
                                            </label>
                                            <textarea
                                              value={(item.content as { system: string }).system || ''}
                                              onChange={(e) => {
                                                const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                                const newSequence = currentSequence.map((seqItem, i) =>
                                                  i === index ? { ...seqItem, content: { ...seqItem.content, system: e.target.value } } : seqItem
                                                );
                                                setFormData(prev => ({ ...prev, messageContent: { sequence: newSequence } }));
                                              }}
                                              placeholder="Ex: Você é um assistente de vendas prestativo..."
                                              rows={2}
                                              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all resize-none placeholder-gray-600 text-white shadow-inner"
                                            />
                                          </div>

                                          <div>
                                            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] mb-2 font-oswald">
                                              Mensagem do Usuário (Prompt)
                                            </label>
                                            <div className="relative">
                                              <textarea
                                                value={(item.content as { user: string }).user || ''}
                                                onChange={(e) => {
                                                  const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                                  const newSequence = currentSequence.map((seqItem, i) =>
                                                    i === index ? { ...seqItem, content: { ...seqItem.content, user: e.target.value } } : seqItem
                                                  );
                                                  setFormData(prev => ({ ...prev, messageContent: { sequence: newSequence } }));
                                                }}
                                                placeholder="Ex: Crie uma mensagem de boas vindas para {{nome}}"
                                                rows={4}
                                                className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all resize-none placeholder-gray-600 text-white shadow-inner"
                                              />
                                              <div className="absolute top-3 right-3">
                                                <div className="group relative">
                                                  <button type="button" className="text-blue-400 hover:text-blue-300 transition-colors">
                                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                                                  </button>
                                                  <div className="absolute right-0 bottom-full mb-3 w-56 bg-black/95 text-white text-[10px] rounded-xl p-3 opacity-0 group-hover:opacity-100 transition-all transform translate-y-2 group-hover:translate-y-0 pointer-events-none z-10 border border-white/10 font-bold uppercase tracking-[0.2em] backdrop-blur-xl shadow-2xl">
                                                    <div className="flex items-center gap-2 mb-1.5 text-blue-400">
                                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                                                      <span>Dica Pro</span>
                                                    </div>
                                                    Você pode usar variáveis como {'{{nome}}'} para personalizar a geração.
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {item.type === 'groq' && (
                                    <div className="space-y-4 animate-fadeIn">
                                      <div className="bg-black/40 rounded-xl p-6 border border-white/10 shadow-xl backdrop-blur-md">
                                        <div className="flex items-center gap-4 mb-6">
                                          <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-orange-700 rounded-2xl flex items-center justify-center text-white shadow-lg border border-white/10 transform hover:rotate-12 transition-transform">
                                            <span className="text-2xl">⚡</span>
                                          </div>
                                          <div>
                                            <h4 className="font-bold text-white font-oswald text-lg uppercase tracking-wide">Configuração Groq</h4>
                                            <p className="text-[10px] text-blue-400 font-bold uppercase tracking-[0.2em]">Inferência ultra-rápida com Llama 3</p>
                                          </div>
                                        </div>

                                        <div className="space-y-5">
                                          <div>
                                            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] mb-2 font-oswald">Modelo</label>
                                            <div className="relative group/select">
                                              <select
                                                value={(item.content as { model: string }).model || 'llama3-8b-8192'}
                                                onChange={(e) => {
                                                  const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                                  const newSequence = currentSequence.map((seqItem, i) =>
                                                    i === index ? { ...seqItem, content: { ...seqItem.content, model: e.target.value } } : seqItem
                                                  );
                                                  setFormData(prev => ({ ...prev, messageContent: { sequence: newSequence } }));
                                                }}
                                                className="w-full px-4 py-2.5 bg-black/40 border border-white/10 rounded-lg text-sm font-bold focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all text-white appearance-none pr-10 font-oswald uppercase tracking-wider cursor-pointer"
                                              >
                                                <option value="llama3-8b-8192" className="bg-[#1e3a5f]">Llama 3 8B (Ultra Rápido)</option>
                                                <option value="llama3-70b-8192" className="bg-[#1e3a5f]">Llama 3 70B (Alta Performance)</option>
                                                <option value="mixtral-8x7b-32768" className="bg-[#1e3a5f]">Mixtral 8x7B (Versátil)</option>
                                                <option value="gemma-7b-it" className="bg-[#1e3a5f]">Gemma 7B IT (Google)</option>
                                              </select>
                                              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 group-hover/select:text-blue-400 transition-colors">
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                                              </div>
                                            </div>
                                          </div>

                                          <div>
                                            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] mb-2 font-oswald">
                                              Instrução do Sistema (System Prompt)
                                            </label>
                                            <textarea
                                              value={(item.content as { system: string }).system || ''}
                                              onChange={(e) => {
                                                const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                                const newSequence = currentSequence.map((seqItem, i) =>
                                                  i === index ? { ...seqItem, content: { ...seqItem.content, system: e.target.value } } : seqItem
                                                );
                                                setFormData(prev => ({ ...prev, messageContent: { sequence: newSequence } }));
                                              }}
                                              placeholder="Ex: Você é um assistente de vendas prestativo..."
                                              rows={2}
                                              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all resize-none placeholder-gray-600 text-white shadow-inner"
                                            />
                                          </div>

                                          <div>
                                            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] mb-2 font-oswald">
                                              Mensagem do Usuário (Prompt)
                                            </label>
                                            <div className="relative">
                                              <textarea
                                                value={(item.content as { user: string }).user || ''}
                                                onChange={(e) => {
                                                  const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                                  const newSequence = currentSequence.map((seqItem, i) =>
                                                    i === index ? { ...seqItem, content: { ...seqItem.content, user: e.target.value } } : seqItem
                                                  );
                                                  setFormData(prev => ({ ...prev, messageContent: { sequence: newSequence } }));
                                                }}
                                                placeholder="Ex: Crie uma mensagem de boas vindas para {{nome}}"
                                                rows={4}
                                                className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all resize-none placeholder-gray-600 text-white shadow-inner"
                                              />
                                              <div className="absolute top-3 right-3">
                                                <div className="group relative">
                                                  <button type="button" className="text-blue-400 hover:text-blue-300 transition-colors">
                                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                                                  </button>
                                                  <div className="absolute right-0 bottom-full mb-3 w-56 bg-black/95 text-white text-[10px] rounded-xl p-3 opacity-0 group-hover:opacity-100 transition-all transform translate-y-2 group-hover:translate-y-0 pointer-events-none z-10 border border-white/10 font-bold uppercase tracking-[0.2em] backdrop-blur-xl shadow-2xl">
                                                    <div className="flex items-center gap-2 mb-1.5 text-blue-400">
                                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                                                      <span>Dica Pro</span>
                                                    </div>
                                                    Você pode usar variáveis como {'{{nome}}'} para personalizar a geração.
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}

                          {(!('sequence' in formData.messageContent) || formData.messageContent.sequence.length === 0) && (
                            <div className="text-center py-16 bg-white/5 rounded-2xl border-2 border-dashed border-white/10 backdrop-blur-sm">
                              <div className="w-16 h-16 bg-blue-600/20 text-blue-400 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg border border-blue-500/30">
                                <span className="text-3xl">📝</span>
                              </div>
                              <h4 className="text-lg font-bold text-white mb-2 font-oswald uppercase tracking-wide">Comece sua sequência</h4>
                              <p className="text-gray-400 font-medium">Adicione mensagens para compor sua campanha.</p>
                            </div>
                          )}
                        </div>
                    </>
                  )}

                  {activeTab === 'summary' && (
                    <div className="space-y-6">
                      <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
                        <label className="block text-sm font-bold text-gray-300 mb-4 uppercase tracking-wide font-oswald border-b border-white/10 pb-2">
                          📋 Resumo da Campanha
                        </label>

                        <div className="space-y-4">
                          <div className="grid grid-cols-5 gap-3">
                            {/* Nome */}
                            <div className="bg-black/20 rounded-xl p-4 flex flex-col justify-between min-h-[90px]">
                              <div className="flex items-center gap-2 mb-2">
                                <svg className="w-4 h-4 text-blue-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                                <span className="text-[9px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">Nome</span>
                              </div>
                              <p className="text-white font-bold font-oswald uppercase tracking-wide text-sm truncate leading-tight">{formData.nome || '—'}</p>
                            </div>

                            {/* Categorias */}
                            <div className="bg-black/20 rounded-xl p-4 flex flex-col min-h-[90px]">
                              <div className="flex items-center gap-2 mb-2">
                                <svg className="w-4 h-4 text-blue-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"/></svg>
                                <span className="text-[9px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">Grupos</span>
                              </div>
                              <div className="flex flex-wrap gap-1.5">
                                {formData.targetTags.length === 0 ? (
                                  <span className="text-gray-500 text-xs">—</span>
                                ) : (
                                  formData.targetTags.slice(0, 2).map(tagId => {
                                    const tag = contactTags.find(t => t.id === tagId);
                                    return tag ? (
                                      <span key={tagId} className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-600/10 border border-blue-500/30 text-blue-300 text-[10px] font-bold font-oswald uppercase tracking-wider">
                                        {tag.nome}
                                      </span>
                                    ) : null;
                                  })
                                )}
                                {formData.targetTags.length > 2 && (
                                  <span className="text-[10px] text-gray-400 font-bold font-oswald">+{formData.targetTags.length - 2}</span>
                                )}
                              </div>
                            </div>

                            {/* Conexões */}
                            <div className="bg-black/20 rounded-xl p-4 flex flex-col min-h-[90px]">
                              <div className="flex items-center gap-2 mb-2">
                                <svg className="w-4 h-4 text-blue-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/></svg>
                                <span className="text-[9px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">Sessões</span>
                              </div>
                              <div className="flex flex-wrap gap-1.5 items-center">
                                {formData.sessionNames.length === 0 ? (
                                  <span className="text-gray-500 text-xs">—</span>
                                ) : (
                                  <span className="flex -space-x-1.5">
                                    {formData.sessionNames.slice(0, 3).map((sessionName, i) => {
                                      const session = whatsappSessions.find(s => s.name === sessionName);
                                      const initial = (session?.mePushName || session?.displayName || sessionName).charAt(0).toUpperCase();
                                      return (
                                        <span key={i} className="w-6 h-6 rounded-full bg-green-900/40 border border-[#0f172a] flex items-center justify-center text-[9px] font-bold text-green-300 shadow" title={sessionName}>
                                          {initial}
                                        </span>
                                      );
                                    })}
                                    {formData.sessionNames.length > 3 && <span className="text-[10px] text-gray-400 font-bold font-oswald ml-1">+{formData.sessionNames.length - 3}</span>}
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Agendamento */}
                            <div className="bg-black/20 rounded-xl p-4 flex flex-col justify-between min-h-[90px]">
                              <div className="flex items-center gap-2 mb-2">
                                <svg className="w-4 h-4 text-blue-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
                                <span className="text-[9px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">Agenda</span>
                              </div>
                              <p className="text-white font-bold text-sm leading-tight">
                                {formData.startImmediately
                                  ? 'Imediato'
                                  : formData.scheduledFor
                                    ? new Date(formData.scheduledFor).toLocaleDateString('pt-BR')
                                    : '—'
                                }
                              </p>
                            </div>

                            {/* Modo de Entrega */}
                            <div className="bg-black/20 rounded-xl p-4 flex flex-col justify-between min-h-[90px]">
                              <div className="flex items-center gap-2 mb-2">
                                <svg className="w-4 h-4 text-blue-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                                <span className="text-[9px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">Entrega</span>
                              </div>
                              <p className="text-white font-bold text-sm leading-tight">
                                {formData.deliveryMode === 'SEQUENCE' ? 'Sequência' : 'Variações'}
                              </p>
                            </div>
                          </div>

                          {/* Parâmetros (Step 2) */}
                          <div className="bg-black/20 rounded-xl p-4">
                            <div className="flex items-center gap-3 mb-3">
                              <div className="w-10 h-10 rounded-xl bg-blue-600/20 flex items-center justify-center text-blue-400 shrink-0">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"/></svg>
                              </div>
                              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">Parâmetros de Envio</span>
                            </div>
                            <div className="grid grid-cols-6 gap-2 ml-0">
                              <div className="bg-white/5 rounded-lg p-3 border border-white/5">
                                <span className="text-[9px] font-bold text-gray-400 uppercase tracking-[0.15em] font-oswald">Pausa</span>
                                <p className="text-white font-bold text-sm mt-1">{formData.randomDelay}s</p>
                              </div>
                              <div className="bg-white/5 rounded-lg p-3 border border-white/5">
                                <span className="text-[9px] font-bold text-gray-400 uppercase tracking-[0.15em] font-oswald">Lote</span>
                                <p className="text-white font-bold text-sm mt-1">{formData.batchSize ?? 'Todos'}</p>
                              </div>
                              <div className="bg-white/5 rounded-lg p-3 border border-white/5">
                                <span className="text-[9px] font-bold text-gray-400 uppercase tracking-[0.15em] font-oswald">Limite</span>
                                <p className="text-white font-bold text-sm mt-1">{formData.dailyCutoff || '—'}</p>
                              </div>
                              <div className="bg-white/5 rounded-lg p-3 border border-white/5">
                                <span className="text-[9px] font-bold text-gray-400 uppercase tracking-[0.15em] font-oswald">Prazo</span>
                                <p className="text-white font-bold text-sm mt-1">{formData.deadlineAt ? new Date(formData.deadlineAt).toLocaleDateString('pt-BR') : '—'}</p>
                              </div>
                              <div className="bg-white/5 rounded-lg p-3 border border-white/5">
                                <span className="text-[9px] font-bold text-gray-400 uppercase tracking-[0.15em] font-oswald">Contatos</span>
                                <p className="text-white font-bold text-sm mt-1">{totalSelectedContacts}</p>
                              </div>
                              <div className="bg-white/5 rounded-lg p-3 border border-white/5">
                                <span className="text-[9px] font-bold text-gray-400 uppercase tracking-[0.15em] font-oswald">Duração</span>
                                <p className="text-white font-bold text-sm mt-1">{etaTotalLabel}</p>
                              </div>
                            </div>
                          </div>

                          {/* Variações de Mensagens */}
                          <div className="bg-black/20 rounded-xl p-4">
                            <div className="flex items-center gap-3 mb-3">
                              <div className="w-10 h-10 rounded-xl bg-blue-600/20 flex items-center justify-center text-blue-400 shrink-0">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"/></svg>
                              </div>
                              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">Mensagens ({('sequence' in formData.messageContent ? formData.messageContent.sequence : []).length})</span>
                            </div>
                            <div className="space-y-2">
                              {('sequence' in formData.messageContent ? formData.messageContent.sequence : []).length === 0 ? (
                                <p className="text-gray-500 text-sm">Nenhuma mensagem configurada</p>
                              ) : (
                                ('sequence' in formData.messageContent ? formData.messageContent.sequence : []).map((item, index) => {
                                  const typeLabels: Record<string, string> = {
                                    text: '💬 Texto',
                                    image: '🖼️ Imagem',
                                    video: '🎬 Vídeo',
                                    audio: '🎵 Áudio',
                                    document: '📄 Arquivo',
                                    wait: '⏱️ Espera',
                                    openai: '🤖 OpenAI',
                                    groq: '⚡ Groq AI'
                                  };
                                  const content = item.content as any;
                                  let variationCount = 0;
                                  let variationLabel = '';
                                  if (item.type === 'text' && content.variations?.length) {
                                    variationCount = content.variations.length;
                                    variationLabel = `${variationCount} variações de texto`;
                                  } else if (['image', 'video', 'audio'].includes(item.type) && content.mediaVariations?.length) {
                                    variationCount = content.mediaVariations.length;
                                    variationLabel = `${variationCount} variações de mídia`;
                                  }
                                  return (
                                    <div key={index} className="flex items-center gap-3 bg-white/5 rounded-lg p-3 border border-white/5">
                                      <span className="w-6 h-6 rounded-md bg-blue-600/20 flex items-center justify-center text-xs font-bold text-blue-400 border border-blue-500/30 font-oswald">
                                        {index + 1}
                                      </span>
                                      <span className="text-sm font-bold text-white font-oswald uppercase tracking-wide flex-1">
                                        {typeLabels[item.type] || item.type}
                                      </span>
                                      {variationCount > 0 && (
                                        <span className="text-[10px] font-bold text-yellow-400 bg-yellow-500/10 px-3 py-1 rounded-full border border-yellow-500/30 font-oswald uppercase tracking-wider">
                                          {variationLabel}
                                        </span>
                                      )}
                                      {item.type === 'wait' && (
                                        <span className="text-[10px] font-bold text-blue-400 bg-blue-500/10 px-3 py-1 rounded-full border border-blue-500/30 font-oswald">
                                          {content.waitTime}s
                                        </span>
                                      )}
                                    </div>
                                  );
                                })
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                </form>
              </div>

              {/* Footer Actions */}
              <div className="p-6 border-t border-white/10 bg-white/5 backdrop-blur-md flex justify-end gap-3 rounded-b-[2rem]">
                {activeTab === 'summary' ? (
                  <>
                    <button
                      type="button"
                      onClick={() => setShowCreateModal(false)}
                      className="px-6 py-3 text-red-400 font-bold uppercase tracking-wide text-xs hover:bg-red-500/10 rounded-xl transition-all border border-red-500/30 hover:border-red-500/50"
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveTab('messages')}
                      className="px-6 py-3 text-green-400 font-bold uppercase tracking-wide text-xs hover:bg-green-500/10 rounded-xl transition-all border border-green-500/30 hover:border-green-500/50"
                    >
                      ← Voltar
                    </button>
                    <button
                      onClick={handleCreateCampaign}
                      className="px-8 py-3 bg-green-600 text-white font-bold uppercase tracking-[0.2em] text-xs rounded-xl shadow-lg hover:shadow-green-500/20 hover:scale-105 transition-all duration-300 flex items-center gap-2 border border-green-500 relative overflow-hidden group/btn"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
                      <span>🚀 Iniciar Campanha</span>
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => setShowCreateModal(false)}
                      className="px-6 py-3 text-red-400 font-bold uppercase tracking-wide text-xs hover:bg-red-500/10 rounded-xl transition-all border border-red-500/30 hover:border-red-500/50"
                    >
                      Cancelar
                    </button>
                    {activeTab === 'general' && (
                      <button
                        type="button"
                        onClick={() => setActiveTab('parameters')}
                        className="px-8 py-3 bg-blue-600 text-white font-bold uppercase tracking-[0.2em] text-xs rounded-xl shadow-lg hover:shadow-blue-500/20 hover:scale-105 transition-all duration-300 flex items-center gap-2 border border-blue-500"
                      >
                        Próximo →
                      </button>
                    )}
                    {activeTab === 'parameters' && (
                      <>
                        <button
                          type="button"
                          onClick={() => setActiveTab('general')}
                          className="px-6 py-3 text-green-400 font-bold uppercase tracking-wide text-xs hover:bg-green-500/10 rounded-xl transition-all border border-green-500/30 hover:border-green-500/50"
                        >
                          ← Voltar
                        </button>
                        <button
                          type="button"
                          onClick={() => setActiveTab('messages')}
                          className="px-8 py-3 bg-blue-600 text-white font-bold uppercase tracking-[0.2em] text-xs rounded-xl shadow-lg hover:shadow-blue-500/20 hover:scale-105 transition-all duration-300 flex items-center gap-2 border border-blue-500"
                        >
                          Próximo →
                        </button>
                      </>
                    )}
                    {activeTab === 'messages' && (
                      <>
                        <button
                          type="button"
                          onClick={() => setActiveTab('parameters')}
                          className="px-6 py-3 text-green-400 font-bold uppercase tracking-wide text-xs hover:bg-green-500/10 rounded-xl transition-all border border-green-500/30 hover:border-green-500/50"
                        >
                          ← Voltar
                        </button>
                        {(() => {
                          const hasMessages = formData.messageContent &&
                            'sequence' in formData.messageContent &&
                            formData.messageContent.sequence.length > 0;

                          if (!hasMessages) {
                            return (
                              <button
                                type="button"
                                disabled
                                className="px-8 py-3 bg-gray-600 text-gray-400 font-bold uppercase tracking-[0.2em] text-xs rounded-xl border border-gray-500 cursor-not-allowed"
                              >
                                Adicione mensagens primeiro
                              </button>
                            );
                          }

                          return (
                            <button
                              type="button"
                              onClick={() => setActiveTab('summary')}
                              className="px-8 py-3 bg-blue-600 text-white font-bold uppercase tracking-[0.2em] text-xs rounded-xl shadow-lg hover:shadow-blue-500/20 hover:scale-105 transition-all duration-300 flex items-center gap-2 border border-blue-500"
                            >
                              Próximo →
                            </button>
                          );
                        })()}
                      </>
                    )}
                  </>
                )}
              </div>

            </div>
          </div>
        </Portal>
      )}

      {/* Modal de Relatórios */}
      {showReportModal && (
        <Portal>
          <div className="fixed inset-0 bg-[#0f172a]/80 flex items-center justify-center backdrop-blur-md p-4" style={{ zIndex: 9999 }}>
            <div className="bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] rounded-[2.5rem] shadow-2xl border border-white/10 max-w-[95vw] w-full max-h-[90vh] flex flex-col relative overflow-hidden group/modal"
              style={{ zIndex: 1000 }}>
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-transparent opacity-0 group-hover/modal:opacity-100 transition-opacity duration-500" />

              <div className="p-8 border-b border-white/10 flex justify-between items-center bg-white/5 backdrop-blur-md relative z-10">
                <div className="flex items-center gap-6">
                  <h3 className="text-3xl font-bold text-white font-oswald uppercase tracking-tight">Relatório da Campanha</h3>
                  <button
                    onClick={handleRefreshReport}
                    disabled={reportLoading}
                    className="flex items-center gap-3 px-6 py-2.5 bg-blue-600/10 text-blue-400 text-[10px] font-bold uppercase tracking-[0.2em] rounded-xl hover:bg-blue-600/20 transition-all border border-blue-500/30 disabled:opacity-50 disabled:cursor-not-allowed group/refresh font-oswald"
                    title="Atualizar relatório"
                  >
                    <svg className={`w-4 h-4 ${reportLoading ? 'animate-spin' : 'group-hover/refresh:rotate-180'} transition-transform duration-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    {reportLoading ? 'Atualizando...' : 'Atualizar'}
                  </button>
                </div>
                <button
                  onClick={() => setShowReportModal(false)}
                  className="w-12 h-12 flex items-center justify-center text-gray-400 hover:text-white transition-all bg-white/5 hover:bg-white/10 rounded-2xl border border-white/10"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-8 flex-1 overflow-hidden flex flex-col relative z-10">
                {reportLoading ? (
                  <div className="flex flex-col items-center justify-center py-24 gap-6">
                    <div className="relative">
                      <div className="w-16 h-16 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin"></div>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-8 h-8 bg-blue-500/10 rounded-full animate-pulse"></div>
                      </div>
                    </div>
                    <span className="text-gray-400 font-oswald uppercase tracking-[0.2em] font-bold text-sm">Carregando relatório...</span>
                  </div>
                ) : reportData ? (
                    <Tabs defaultValue="overview" className="flex-1 flex flex-col overflow-hidden">
                      <TabsList className="grid w-full grid-cols-3 mb-8 bg-white/5 p-1.5 rounded-2xl border border-white/10 backdrop-blur-3xl">
                        <TabsTrigger
                          value="overview"
                          className="data-[state=active]:bg-blue-600 data-[state=active]:text-white text-gray-400 hover:text-white rounded-xl transition-all font-oswald uppercase tracking-[0.2em] py-3.5 text-[10px] font-bold relative overflow-hidden group/tab"
                        >
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover/tab:animate-shimmer" />
                          Visão Geral
                        </TabsTrigger>
                        <TabsTrigger
                          value="campaign-details"
                          className="data-[state=active]:bg-blue-600 data-[state=active]:text-white text-gray-400 hover:text-white rounded-xl transition-all font-oswald uppercase tracking-[0.2em] py-3.5 text-[10px] font-bold relative overflow-hidden group/tab"
                        >
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover/tab:animate-shimmer" />
                          Detalhes da Campanha
                        </TabsTrigger>
                        <TabsTrigger
                          value="details"
                          className="data-[state=active]:bg-blue-600 data-[state=active]:text-white text-gray-400 hover:text-white rounded-xl transition-all font-oswald uppercase tracking-[0.2em] py-3.5 text-[10px] font-bold relative overflow-hidden group/tab"
                        >
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover/tab:animate-shimmer" />
                          Detalhes do Envio
                        </TabsTrigger>
                      </TabsList>

                    <TabsContent value="overview" className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
                      <div className="space-y-8">
                        {/* Informações da Campanha */}
                        <div className="bg-white/5 border border-white/10 rounded-[2rem] p-8 backdrop-blur-md relative overflow-hidden group/card hover:border-white/20 transition-all">
                          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600/5 rounded-full blur-[80px] -mr-32 -mt-32 group-hover/card:bg-blue-600/10 transition-colors" />

                          <h4 className="text-2xl font-bold text-white mb-8 flex items-center gap-4 font-oswald uppercase tracking-tight">
                            <span className="w-2 h-8 bg-blue-600 rounded-full shadow-[0_0_15px_rgba(37,99,235,0.5)]"></span>
                            {reportData.campaign.nome}
                          </h4>

                          <div className="grid grid-cols-2 md:grid-cols-3 gap-10 text-sm relative z-10">
                            <div className="space-y-2">
                              <span className="text-[10px] font-bold text-gray-400 font-oswald uppercase tracking-[0.2em] block">Status</span>
                              <span className={`inline-flex items-center px-4 py-1.5 rounded-full text-[10px] font-bold font-oswald uppercase tracking-[0.2em] border shadow-sm ${getStatusColor(reportData.campaign.status)}`}>
                                {getStatusText(reportData.campaign.status)}
                              </span>
                            </div>
                            <div className="space-y-2">
                              <span className="text-[10px] font-bold text-gray-400 font-oswald uppercase tracking-[0.2em] block">Criado por</span>
                              <span className="text-white font-bold text-base tracking-wide uppercase font-oswald">
                                {reportData.campaign.createdByName || 'N/A'}
                              </span>
                            </div>
                            <div className="space-y-2">
                              <span className="text-[10px] font-bold text-gray-400 font-oswald uppercase tracking-[0.2em] block">Data de Criação</span>
                              <span className="text-white font-bold text-base tracking-wide uppercase font-oswald">
                                {reportData.campaign.criadoEm ? new Date(reportData.campaign.criadoEm).toLocaleString('pt-BR') : 'N/A'}
                              </span>
                            </div>
                            <div className="space-y-2">
                              <span className="text-[10px] font-bold text-gray-400 font-oswald uppercase tracking-[0.2em] block">Data de Início</span>
                              <span className="text-white font-bold text-base tracking-wide uppercase font-oswald">
                                {reportData.campaign.startedAt ? new Date(reportData.campaign.startedAt).toLocaleString('pt-BR') : 'Aguardando...'}
                              </span>
                            </div>
                            <div className="space-y-2">
                              <span className="text-[10px] font-bold text-gray-400 font-oswald uppercase tracking-[0.2em] block">Conclusão</span>
                              <span className="text-white font-bold text-base tracking-wide uppercase font-oswald">
                                {reportData.campaign.completedAt ? new Date(reportData.campaign.completedAt).toLocaleString('pt-BR') : 'Em andamento'}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Estatísticas */}
                        <div>
                          <h4 className="text-xl font-bold text-white mb-6 font-oswald uppercase tracking-tight flex items-center gap-4">
                            <span className="w-2 h-6 bg-blue-600 rounded-full"></span>
                            Estatísticas de Envio
                          </h4>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                            <div className="bg-white/5 border border-white/10 rounded-[2rem] p-6 text-center backdrop-blur-md hover:bg-white/10 transition-all group/stat relative overflow-hidden">
                              <div className="absolute inset-0 bg-gradient-to-b from-blue-500/5 to-transparent opacity-0 group-hover/stat:opacity-100 transition-opacity" />
                              <div className="text-4xl font-bold text-blue-400 mb-2 relative z-10 font-oswald">{reportData.stats?.total || 0}</div>
                              <div className="text-[10px] font-bold text-gray-400 font-oswald uppercase tracking-[0.2em] relative z-10">Total Contatos</div>
                            </div>
                            <div className="bg-white/5 border border-white/10 rounded-[2rem] p-6 text-center backdrop-blur-md hover:bg-white/10 transition-all group/stat relative overflow-hidden">
                              <div className="absolute inset-0 bg-gradient-to-b from-green-500/5 to-transparent opacity-0 group-hover/stat:opacity-100 transition-opacity" />
                              <div className="text-4xl font-bold text-green-400 mb-2 relative z-10 font-oswald">{reportData.stats?.sent || 0}</div>
                              <div className="text-[10px] font-bold text-gray-400 font-oswald uppercase tracking-[0.2em] relative z-10">Enviadas</div>
                            </div>
                            <div className="bg-white/5 border border-white/10 rounded-[2rem] p-6 text-center backdrop-blur-md hover:bg-white/10 transition-all group/stat relative overflow-hidden">
                              <div className="absolute inset-0 bg-gradient-to-b from-red-500/5 to-transparent opacity-0 group-hover/stat:opacity-100 transition-opacity" />
                              <div className="text-4xl font-bold text-red-400 mb-2 relative z-10 font-oswald">{reportData.stats?.failed || 0}</div>
                              <div className="text-[10px] font-bold text-gray-400 font-oswald uppercase tracking-[0.2em] relative z-10">Falhas</div>
                            </div>
                            <div className="bg-white/5 border border-white/10 rounded-[2rem] p-6 text-center backdrop-blur-md hover:bg-white/10 transition-all group/stat relative overflow-hidden">
                              <div className="absolute inset-0 bg-gradient-to-b from-yellow-500/5 to-transparent opacity-0 group-hover/stat:opacity-100 transition-opacity" />
                              <div className="text-4xl font-bold text-yellow-400 mb-2 relative z-10 font-oswald">{reportData.stats?.pending || 0}</div>
                              <div className="text-[10px] font-bold text-gray-400 font-oswald uppercase tracking-[0.2em] relative z-10">Pendentes</div>
                            </div>
                          </div>
                        </div>

                        {reportData.failureReasons && reportData.failureReasons.length > 0 && (
                          <div>
                            <h4 className="text-xl font-bold text-white mb-6 font-oswald uppercase tracking-tight flex items-center gap-4">
                              <span className="w-2 h-6 bg-red-500 rounded-full"></span>
                              Motivos das Falhas
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                              {reportData.failureReasons.slice(0, 6).map((item) => (
                                <div
                                  key={item.reason}
                                  className="bg-red-500/10 border border-red-500/20 rounded-[2rem] p-6 backdrop-blur-md shadow-xl"
                                >
                                  <div className="flex items-start justify-between gap-4">
                                    <div className="min-w-0">
                                      <div className="text-[10px] font-bold text-red-300 uppercase tracking-[0.2em] font-oswald mb-2">
                                        Motivo
                                      </div>
                                      <div className="text-sm text-white leading-relaxed break-words">
                                        {item.reason}
                                      </div>
                                    </div>
                                    <div className="shrink-0 px-4 py-2 rounded-2xl bg-red-500/20 text-red-200 text-xs font-bold uppercase tracking-[0.2em] font-oswald border border-red-500/20">
                                      {item.count}x
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Mensagens por Sessão */}
                        {Object.keys(reportData.messagesBySession).length > 0 && (
                          <div>
                            <h4 className="text-xl font-bold text-white mb-6 font-oswald uppercase tracking-tight flex items-center gap-4">
                              <span className="w-2 h-6 bg-blue-600 rounded-full"></span>
                              Distribuição por Sessão
                            </h4>
                            <div className="bg-white/5 border border-white/10 rounded-[2rem] overflow-hidden backdrop-blur-md shadow-2xl">
                              <table className="min-w-full divide-y divide-white/10">
                                <thead className="bg-white/5">
                                  <tr>
                                    <th className="px-8 py-5 text-left text-[10px] font-bold text-gray-400 font-oswald uppercase tracking-[0.2em]">Sessão</th>
                                    <th className="px-8 py-5 text-left text-[10px] font-bold text-gray-400 font-oswald uppercase tracking-[0.2em]">Total</th>
                                    <th className="px-8 py-5 text-left text-[10px] font-bold text-gray-400 font-oswald uppercase tracking-[0.2em]">Enviadas</th>
                                    <th className="px-8 py-5 text-left text-[10px] font-bold text-gray-400 font-oswald uppercase tracking-[0.2em]">Falhas</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-white/5">
                                  {Object.entries(reportData.messagesBySession).map(([sessionName, sessionData]) => {
                                    const messages = sessionData.messages || [];
                                    return (
                                      <tr key={sessionName} className="hover:bg-white/[0.02] transition-colors group">
                                        <td className="px-8 py-5 whitespace-nowrap text-sm font-bold text-white font-oswald uppercase tracking-wide">{sessionName}</td>
                                        <td className="px-8 py-5 whitespace-nowrap text-sm text-gray-400 font-medium font-oswald">{messages.length}</td>
                                        <td className="px-8 py-5 whitespace-nowrap text-sm text-green-400 font-bold font-oswald">{messages.filter((m: MessageReport) => m.status === 'SENT').length}</td>
                                        <td className="px-8 py-5 whitespace-nowrap text-sm text-red-400 font-bold font-oswald">{messages.filter((m: MessageReport) => m.status === 'FAILED').length}</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    </TabsContent>

                    <TabsContent value="campaign-details" className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
                      <div className="space-y-6">
                        <div className="bg-white/5 border border-white/10 rounded-[2rem] p-8 backdrop-blur-md">
                          <h4 className="text-2xl font-bold text-white mb-8 flex items-center gap-4 font-oswald uppercase tracking-tight">
                            <span className="w-2 h-8 bg-blue-600 rounded-full shadow-[0_0_15px_rgba(37,99,235,0.5)]"></span>
                            {reportData.campaign.nome}
                          </h4>

                          <div className="space-y-4">
                            {/* Categorias */}
                            <div className="flex items-start gap-4 bg-black/20 rounded-xl p-4">
                              <div className="w-10 h-10 rounded-xl bg-blue-600/20 flex items-center justify-center text-blue-400 shrink-0">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"/></svg>
                              </div>
                              <div className="flex-1">
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">Categorias de Contato</span>
                                <div className="flex flex-wrap gap-2 mt-2">
                                  {(!reportData.campaign.targetTags || reportData.campaign.targetTags.length === 0) ? (
                                    <span className="text-gray-500 text-sm">Nenhuma categoria</span>
                                  ) : (
                                    reportData.campaign.targetTags.map(tagId => {
                                      const tag = contactTags.find(t => t.id === tagId);
                                      return tag ? (
                                        <span key={tagId} className="inline-flex items-center px-3 py-1 rounded-full bg-blue-600/10 border border-blue-500/30 text-blue-300 text-xs font-bold font-oswald uppercase tracking-wider">
                                          {tag.nome}
                                        </span>
                                      ) : (
                                        <span key={tagId} className="inline-flex items-center px-3 py-1 rounded-full bg-blue-600/10 border border-blue-500/30 text-blue-300 text-xs font-bold font-oswald uppercase tracking-wider">
                                          {tagId}
                                        </span>
                                      );
                                    })
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Conexões */}
                            <div className="flex items-start gap-4 bg-black/20 rounded-xl p-4">
                              <div className="w-10 h-10 rounded-xl bg-blue-600/20 flex items-center justify-center text-blue-400 shrink-0">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/></svg>
                              </div>
                              <div className="flex-1">
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">Conexões WhatsApp</span>
                                <div className="flex flex-wrap gap-2 mt-2">
                                  {(!reportData.campaign.sessionNames || reportData.campaign.sessionNames.length === 0) ? (
                                    <span className="text-gray-500 text-sm">Nenhuma conexão</span>
                                  ) : (
                                    reportData.campaign.sessionNames.map(sessionName => {
                                      const sessionInfo = reportData.sessionsInfo?.[sessionName];
                                      const displayName = sessionInfo?.mePushName || whatsappSessions.find(s => s.name === sessionName)?.mePushName || sessionName;
                                      return (
                                        <span key={sessionName} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-600/10 border border-green-500/30 text-green-300 text-xs font-bold font-oswald uppercase tracking-wider">
                                          <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                                          {displayName}
                                        </span>
                                      );
                                    })
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Agendamento */}
                            <div className="flex items-center gap-4 bg-black/20 rounded-xl p-4">
                              <div className="w-10 h-10 rounded-xl bg-blue-600/20 flex items-center justify-center text-blue-400 shrink-0">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
                              </div>
                              <div className="flex-1">
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">Agendamento</span>
                                <p className="text-white font-bold mt-1">
                                  {reportData.campaign.startImmediately
                                    ? '🚀 Imediato'
                                    : reportData.campaign.scheduledFor
                                      ? `📅 ${new Date(reportData.campaign.scheduledFor).toLocaleString('pt-BR')}`
                                      : '📅 Não definido'
                                  }
                                </p>
                              </div>
                            </div>

                            {/* Parâmetros */}
                            <div className="bg-black/20 rounded-xl p-4">
                              <div className="flex items-center gap-3 mb-3">
                                <div className="w-10 h-10 rounded-xl bg-blue-600/20 flex items-center justify-center text-blue-400 shrink-0">
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"/></svg>
                                </div>
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">Parâmetros de Envio</span>
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                <div className="bg-white/5 rounded-lg p-3 border border-white/5">
                                  <span className="text-[9px] font-bold text-gray-400 uppercase tracking-[0.15em] font-oswald">Pausa Aleatória</span>
                                  <p className="text-white font-bold text-sm mt-1">{reportData.campaign.randomDelay ?? '—'}s</p>
                                </div>
                                <div className="bg-white/5 rounded-lg p-3 border border-white/5">
                                  <span className="text-[9px] font-bold text-gray-400 uppercase tracking-[0.15em] font-oswald">Lote (Batch)</span>
                                  <p className="text-white font-bold text-sm mt-1">{reportData.campaign.batchSize ?? 'Enviar tudo'}</p>
                                </div>
                                <div className="bg-white/5 rounded-lg p-3 border border-white/5">
                                  <span className="text-[9px] font-bold text-gray-400 uppercase tracking-[0.15em] font-oswald">Horário Máximo</span>
                                  <p className="text-white font-bold text-sm mt-1">{reportData.campaign.dailyCutoff || 'Sem limite'}</p>
                                </div>
                                <div className="bg-white/5 rounded-lg p-3 border border-white/5">
                                  <span className="text-[9px] font-bold text-gray-400 uppercase tracking-[0.15em] font-oswald">Deadline</span>
                                  <p className="text-white font-bold text-sm mt-1">{reportData.campaign.deadlineAt ? new Date(reportData.campaign.deadlineAt).toLocaleString('pt-BR') : 'Sem prazo'}</p>
                                </div>
                              </div>
                            </div>

                            {/* Mensagens */}
                            <div className="bg-black/20 rounded-xl p-4">
                              <div className="flex items-center gap-3 mb-3">
                                <div className="w-10 h-10 rounded-xl bg-blue-600/20 flex items-center justify-center text-blue-400 shrink-0">
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"/></svg>
                                </div>
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">
                                  Mensagens ({(() => {
                                    const mc = reportData.campaign.messageContent;
                                    if (mc && 'sequence' in mc) return mc.sequence.length;
                                    return reportData.campaign.messageType ? 1 : 0;
                                  })()})
                                </span>
                              </div>
                              <div className="space-y-2">
                                {(() => {
                                  const mc = reportData.campaign.messageContent;
                                  const sequence = mc && 'sequence' in mc ? mc.sequence : [];
                                  const typeLabels: Record<string, string> = {
                                    text: '💬 Texto',
                                    image: '🖼️ Imagem',
                                    video: '🎬 Vídeo',
                                    audio: '🎵 Áudio',
                                    document: '📄 Arquivo',
                                    wait: '⏱️ Espera',
                                    openai: '🤖 OpenAI',
                                    groq: '⚡ Groq AI'
                                  };

                                  if (sequence.length === 0) {
                                    const singleType = reportData.campaign.messageType || 'text';
                                    return (
                                      <div className="flex items-center gap-3 bg-white/5 rounded-lg p-3 border border-white/5">
                                        <span className="w-6 h-6 rounded-md bg-blue-600/20 flex items-center justify-center text-xs font-bold text-blue-400 border border-blue-500/30 font-oswald">1</span>
                                        <span className="text-sm font-bold text-white font-oswald uppercase tracking-wide flex-1">
                                          {typeLabels[singleType] || singleType}
                                        </span>
                                      </div>
                                    );
                                  }

                                  return sequence.map((item, index) => {
                                    const content = item.content as any;
                                    let variationCount = 0;
                                    let variationLabel = '';
                                    if (item.type === 'text' && content.variations?.length) {
                                      variationCount = content.variations.length;
                                      variationLabel = `${variationCount} variações de texto`;
                                    } else if (['image', 'video', 'audio'].includes(item.type) && content.mediaVariations?.length) {
                                      variationCount = content.mediaVariations.length;
                                      variationLabel = `${variationCount} variações de mídia`;
                                    }
                                    return (
                                      <div key={index} className="flex items-center gap-3 bg-white/5 rounded-lg p-3 border border-white/5">
                                        <span className="w-6 h-6 rounded-md bg-blue-600/20 flex items-center justify-center text-xs font-bold text-blue-400 border border-blue-500/30 font-oswald">
                                          {index + 1}
                                        </span>
                                        <span className="text-sm font-bold text-white font-oswald uppercase tracking-wide flex-1">
                                          {typeLabels[item.type] || item.type}
                                        </span>
                                        {variationCount > 0 && (
                                          <span className="text-[10px] font-bold text-yellow-400 bg-yellow-500/10 px-3 py-1 rounded-full border border-yellow-500/30 font-oswald uppercase tracking-wider">
                                            {variationLabel}
                                          </span>
                                        )}
                                        {item.type === 'wait' && (
                                          <span className="text-[10px] font-bold text-blue-400 bg-blue-500/10 px-3 py-1 rounded-full border border-blue-500/30 font-oswald">
                                            {content.waitTime}s
                                          </span>
                                        )}
                                      </div>
                                    );
                                  });
                                })()}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </TabsContent>

                    <TabsContent value="details" className="flex-1 overflow-hidden flex flex-col">
                      <div className="flex flex-col md:flex-row justify-between md:items-center gap-6 mb-8">
                        <h4 className="text-xl font-bold text-white uppercase tracking-tight flex items-center gap-4 font-oswald">
                          <span className="w-2 h-6 bg-blue-600 rounded-full shadow-[0_0_15px_rgba(37,99,235,0.5)]"></span>
                          Detalhes das Mensagens
                        </h4>
                        <button
                          onClick={handleDownloadReport}
                          className="px-10 py-4 bg-blue-600 text-white text-[10px] rounded-2xl font-bold shadow-lg shadow-blue-900/40 hover:scale-105 transition-all flex items-center justify-center gap-4 uppercase tracking-[0.2em] border border-blue-500 relative overflow-hidden group/btn font-oswald"
                        >
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
                          <span className="text-lg group-hover:scale-125 transition-transform duration-500">📊</span> DOWNLOAD RELATÓRIO
                        </button>
                      </div>

                      {(() => {
                        const messages = reportData.campaign.messages || [];
                        const totalPages = Math.ceil(messages.length / reportItemsPerPage);
                        const startIndex = (reportCurrentPage - 1) * reportItemsPerPage;
                        const endIndex = startIndex + reportItemsPerPage;
                        const currentMessages = messages.slice(startIndex, endIndex);

                        return (
                          <>
                            <div className="bg-white/5 border border-white/10 rounded-[2.5rem] overflow-hidden flex-1 flex flex-col backdrop-blur-3xl shadow-2xl relative group/table-container">
                              <div className="absolute inset-0 bg-gradient-to-br from-blue-600/5 via-transparent to-blue-900/5 pointer-events-none group-hover/table-container:opacity-100 opacity-50 transition-opacity duration-700" />
                              <div className="overflow-y-auto flex-1 custom-scrollbar relative z-10">
                                <table className="min-w-full divide-y divide-white/10">
                                  <thead className="bg-[#0f172a]/80 sticky top-0 z-20 backdrop-blur-3xl border-b border-white/10">
                                    <tr>
                                      <th className="px-8 py-6 text-left text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">Nome</th>
                                      <th className="px-8 py-6 text-left text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">Telefone</th>
                                      <th className="px-8 py-6 text-left text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">Status</th>
                                      <th className="px-8 py-6 text-left text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">Erro</th>
                                      <th className="px-8 py-6 text-left text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">Data de Envio</th>
                                      <th className="px-8 py-6 text-left text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">Conexão</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-white/5">
                                    {currentMessages.map((message: MessageReport) => (
                                      <tr key={message.id} className="hover:bg-white/[0.03] transition-all duration-300 group/row">
                                        <td className="px-8 py-5 whitespace-nowrap text-[11px] font-bold text-white uppercase tracking-wider font-oswald group-hover/row:text-blue-400 transition-colors">{message.contactName}</td>
                                        <td className="px-8 py-5 whitespace-nowrap text-[11px] text-gray-400 font-bold font-oswald group-hover/row:text-gray-300 transition-colors tracking-widest">{message.contactPhone}</td>
                                        <td className="px-8 py-5 whitespace-nowrap">
                                          <span className={`inline-flex items-center px-5 py-2 rounded-full text-[9px] font-bold uppercase tracking-[0.2em] border shadow-sm font-oswald transition-all group-hover/row:scale-105 ${getStatusColor(message.status)}`}>
                                            {getStatusText(message.status)}
                                          </span>
                                        </td>
                                        <td className="px-8 py-5 whitespace-nowrap">
                                          {message.errorMessage && message.errorMessage !== 'N/A' ? (
                                            (() => {
                                              const errorInfo = getErrorIcon(message.errorMessage);
                                              return errorInfo ? (
                                                <div className="group relative inline-flex items-center">
                                                  <div className={`flex items-center justify-center w-9 h-9 rounded-2xl ${errorInfo.bgColor} cursor-help border border-white/10 shadow-lg group-hover:scale-110 group-hover:rotate-6 transition-all duration-500`}>
                                                    <span className="text-lg">{errorInfo.icon}</span>
                                                  </div>

                                                  {/* Tooltip com posicionamento inteligente */}
                                                  <div className="group-hover:block hidden absolute z-[9999]">
                                                    {/* Versão à esquerda (padrão para não sair da tela) */}
                                                    <div className="absolute right-full top-1/2 transform -translate-y-1/2 mr-4 w-96 max-w-sm p-5 bg-[#0f172a]/95 backdrop-blur-xl text-white rounded-[2rem] shadow-2xl border border-white/10 animate-in fade-in zoom-in-95 duration-200">
                                                      {/* Seta direita */}
                                                      <div className="absolute top-1/2 right-0 transform -translate-y-1/2 translate-x-1">
                                                        <div className="w-2 h-2 bg-[#0f172a]/95 border-r border-t border-white/10 rotate-45"></div>
                                                      </div>

                                                      {/* Conteúdo */}
                                                      <div className="space-y-3 font-oswald">
                                                        <div className="flex items-center gap-2">
                                                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${errorInfo.bgColor} ${errorInfo.color} border border-white/10`}>
                                                            {errorInfo.icon} {errorInfo.category}
                                                          </span>
                                                        </div>
                                                        <div className="font-bold text-white text-xs uppercase tracking-[0.2em] opacity-50">
                                                          Detalhes do Erro:
                                                        </div>
                                                        <div className="text-gray-300 break-all whitespace-normal text-xs leading-relaxed tracking-wide">
                                                          {message.errorMessage}
                                                        </div>
                                                      </div>
                                                    </div>
                                                  </div>
                                                </div>
                                              ) : (
                                                <span className="text-sm text-red-400 font-bold font-oswald uppercase tracking-wide group-hover/row:text-red-300 transition-colors">
                                                  {message.errorMessage}
                                                </span>
                                              );
                                            })()
                                          ) : (
                                            <span className="text-sm text-gray-500 flex items-center">
                                              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-500/10 border border-green-500/20 shadow-sm shadow-green-900/20 group-hover/row:scale-110 transition-transform">
                                                <span className="text-xs">✅</span>
                                              </div>
                                            </span>
                                          )}
                                        </td>
                                        <td className="px-8 py-5 whitespace-nowrap text-sm text-gray-400 font-medium font-oswald group-hover/row:text-gray-300 transition-colors">
                                          {message.sentAt ? new Date(message.sentAt).toLocaleString('pt-BR') : 'N/A'}
                                        </td>
                                        <td className="px-8 py-5 whitespace-nowrap text-sm text-white font-bold font-oswald uppercase tracking-wide group-hover/row:text-blue-400 transition-colors">{message.sessionName || 'N/A'}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>

                                {messages.length === 0 && (
                                  <div className="flex flex-col items-center justify-center py-24 gap-6 relative z-10">
                                    <div className="w-20 h-20 rounded-[2rem] bg-white/5 flex items-center justify-center border border-white/10 shadow-inner group/empty-icon">
                                      <span className="text-4xl opacity-50 group-hover/empty-icon:scale-110 group-hover/empty-icon:rotate-12 transition-transform duration-500">📭</span>
                                    </div>
                                    <div className="text-center space-y-2">
                                      <span className="text-gray-400 font-oswald uppercase tracking-[0.2em] font-bold text-sm block">Nenhuma mensagem encontrada</span>
                                      <p className="text-gray-500 font-oswald text-[10px] font-bold uppercase tracking-[0.15em]">Não há registros de disparos para esta campanha.</p>
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* Paginação */}
                              {totalPages > 1 && (
                                <div className="flex flex-col md:flex-row items-center justify-between mt-10 gap-8 pt-10 border-t border-white/5 relative z-10">
                                  <div className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald bg-white/5 px-8 py-4 rounded-[1.5rem] border border-white/10 backdrop-blur-3xl shadow-inner group/info">
                                    Mostrando <span className="text-white group-hover:text-blue-400 transition-colors">{startIndex + 1}</span> a <span className="text-white group-hover:text-blue-400 transition-colors">{Math.min(endIndex, messages.length)}</span> de <span className="text-blue-400">{messages.length}</span> mensagens
                                  </div>
                                  <div className="flex items-center gap-4">
                                    <button
                                      onClick={() => setReportCurrentPage(page => Math.max(page - 1, 1))}
                                      disabled={reportCurrentPage === 1}
                                      className="px-8 py-4 bg-white/5 text-gray-400 text-[10px] font-bold uppercase tracking-[0.2em] rounded-2xl hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed border border-white/10 transition-all font-oswald flex items-center gap-4 group/btn relative overflow-hidden"
                                    >
                                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
                                      <svg className="w-5 h-5 group-hover:-translate-x-1.5 transition-transform duration-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                                      </svg>
                                      Anterior
                                    </button>

                                    <div className="flex items-center gap-3">
                                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
                                        <button
                                          key={pageNum}
                                          onClick={() => setReportCurrentPage(pageNum)}
                                          className={`w-12 h-12 flex items-center justify-center text-[11px] font-bold uppercase tracking-[0.2em] rounded-2xl transition-all duration-500 font-oswald relative overflow-hidden group/btn ${pageNum === reportCurrentPage
                                            ? 'bg-blue-600 text-white shadow-[0_0_25px_rgba(37,99,235,0.5)] border border-blue-400/50 scale-115 z-10'
                                            : 'bg-white/5 text-gray-400 hover:bg-white/10 border border-white/10 hover:scale-110'
                                            }`}
                                        >
                                          {pageNum === reportCurrentPage && (
                                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
                                          )}
                                          {pageNum}
                                        </button>
                                      ))}
                                    </div>

                                    <button
                                      onClick={() => setReportCurrentPage(page => Math.min(page + 1, totalPages))}
                                      disabled={reportCurrentPage === totalPages}
                                      className="px-8 py-4 bg-white/5 text-gray-400 text-[10px] font-bold uppercase tracking-[0.2em] rounded-2xl hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed border border-white/10 transition-all font-oswald flex items-center gap-4 group/btn relative overflow-hidden"
                                    >
                                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
                                      Próximo
                                      <svg className="w-5 h-5 group-hover:translate-x-1.5 transition-transform duration-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                      </svg>
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </>
                        );
                      })()}
                    </TabsContent>
                  </Tabs>
                ) : (
                  <div className="flex flex-col items-center justify-center py-20 gap-6 bg-red-500/5 rounded-[2.5rem] border border-red-500/20 backdrop-blur-md relative overflow-hidden group/error">
                    <div className="absolute inset-0 bg-gradient-to-br from-red-500/5 to-transparent opacity-0 group-hover/error:opacity-100 transition-opacity" />
                    <div className="w-20 h-20 rounded-2xl bg-red-500/10 flex items-center justify-center border border-red-500/20 shadow-[0_0_30px_rgba(239,68,68,0.1)] group-hover:scale-110 transition-transform duration-500">
                      <span className="text-3xl animate-pulse">⚠️</span>
                    </div>
                    <div className="flex flex-col items-center gap-3 relative z-10">
                      <span className="text-red-400 font-oswald uppercase tracking-[0.2em] font-bold text-lg">Erro ao carregar relatório</span>
                      <p className="text-gray-400 text-[10px] font-bold uppercase tracking-[0.15em] text-center max-w-[280px] font-oswald leading-relaxed">
                        Ocorreu um problema ao buscar os dados da campanha. Por favor, verifique sua conexão e tente novamente.
                      </p>
                    </div>
                    <button
                      onClick={handleRefreshReport}
                      className="mt-4 px-8 py-3 bg-red-500/10 text-red-400 text-[10px] font-bold uppercase tracking-[0.2em] rounded-xl hover:bg-red-500/20 transition-all border border-red-500/30 font-oswald"
                    >
                      Tentar Novamente
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Portal>
      )}

      {/* Diálogo de Confirmação Padronizado */}
      <AlertDialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <AlertDialogContent className="bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] border border-white/10 rounded-[2.5rem] p-8 backdrop-blur-3xl shadow-2xl max-w-md">
          <AlertDialogHeader className="space-y-4">
            <AlertDialogTitle className="text-2xl font-bold font-oswald text-white uppercase tracking-tight flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center border ${confirmDialogConfig.type === 'danger' ? 'bg-red-500/10 border-red-500/20 text-red-400' : 'bg-blue-500/10 border-blue-500/20 text-blue-400'}`}>
                {confirmDialogConfig.type === 'danger' ? '⚠️' : 'ℹ️'}
              </div>
              {confirmDialogConfig.title}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400 font-oswald uppercase tracking-[0.1em] text-[10px] font-bold leading-relaxed">
              {confirmDialogConfig.message}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-8 flex gap-3">
            <AlertDialogCancel className="flex-1 px-8 py-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl text-[10px] font-bold font-oswald text-gray-400 uppercase tracking-[0.2em] transition-all">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDialogConfig.onConfirm}
              className={`flex-1 px-8 py-4 rounded-2xl text-[10px] font-bold font-oswald text-white uppercase tracking-[0.2em] transition-all border shadow-lg ${confirmDialogConfig.type === 'danger'
                ? 'bg-red-600 hover:bg-red-500 border-red-500 shadow-red-900/20'
                : 'bg-blue-600 hover:bg-blue-500 border-blue-500 shadow-blue-900/20'
                }`}
            >
              {confirmDialogConfig.confirmText || 'Confirmar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
